(function attachPdp(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.SaikoSpeedPdp = api;
    if (root.document) api.initializeBrowser(root.document, root);
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function createPdpApi() {
  'use strict';

  function vehicleLabel(vehicle) {
    if (!vehicle) return '';
    return [vehicle.year, vehicle.make, vehicle.model, vehicle.submodel]
      .filter(Boolean)
      .join(' ');
  }

  function normalizeCents(value) {
    const cents = Number(value);
    return Number.isFinite(cents) ? Math.max(0, Math.round(cents)) : 0;
  }

  function normalizePercentage(value) {
    const percentage = Number(value);
    return Number.isFinite(percentage)
      ? Math.min(100, Math.max(0, percentage))
      : 0;
  }

  function calculateMemberPriceCents(basePriceCents, discountPercentage) {
    const baseCents = normalizeCents(basePriceCents);
    const percentage = normalizePercentage(discountPercentage);
    const savingsCents = Math.floor(baseCents * percentage / 100);
    return {
      priceCents: baseCents - savingsCents,
      savingsCents
    };
  }

  function createLatestRequestGate() {
    let version = 0;
    return {
      invalidate() {
        version += 1;
      },
      run(operation) {
        const requestVersion = ++version;
        return operation(() => requestVersion === version);
      }
    };
  }

  function createAbortableLatestRequestGate(AbortControllerRef) {
    const Controller = AbortControllerRef
      || (typeof AbortController !== 'undefined' ? AbortController : null);
    let version = 0;
    let controller = null;

    return {
      invalidate() {
        version += 1;
        if (controller) controller.abort();
        controller = null;
      },
      next() {
        version += 1;
        if (controller) controller.abort();
        controller = Controller ? new Controller() : { signal: undefined, abort() {} };
        const requestVersion = version;
        return {
          signal: controller.signal,
          isCurrent: () => requestVersion === version
        };
      }
    };
  }

  function getPurchaseState(variant) {
    if (!variant) {
      return { purchasable: false, label: 'Choose an option', inventory: '' };
    }

    const quantity = Number(
      typeof variant.quantity === 'number'
        ? variant.quantity
        : variant.inventory_quantity
    );
    const policy = variant.inventoryPolicy
      || variant.policy
      || variant.inventory_policy
      || '';
    const inventoryTracked = variant.inventory_management != null;

    if (variant.available === true && (!inventoryTracked || quantity > 0)) {
      // The variant payload clamps quantities at 10, so 10 reads as "10+";
      // anything lower is the exact count - factual scarcity, never fake.
      // Untracked SKUs with no stock on hand ship from the manufacturer.
      let inventory;
      if (inventoryTracked) {
        inventory = quantity >= 10 ? '10+ in stock' : `${quantity} in stock`;
      } else {
        inventory = quantity > 0 ? 'Available' : 'Ships from manufacturer';
      }
      return {
        purchasable: true,
        label: 'Add to cart',
        inventory
      };
    }
    if (policy === 'continue') {
      // Tracked at zero but continue-policy: the shelf is empty and
      // ordering is the backorder path. Purchasable, so the full
      // sentence leads the buy box and the mirror carries the compact.
      return {
        purchasable: true,
        label: 'Add to cart',
        inventory: 'Out of stock. Order to place a backorder.',
        inventoryCompact: 'Out of stock'
      };
    }
    // The full sentence leads the buy box; the compact form fits the
    // sticky mirror. Backorders on deny-policy SKUs route through a human.
    return {
      purchasable: false,
      label: 'Sold out',
      inventory: 'Out of stock. Contact us to place a backorder or get an ETA.',
      inventoryCompact: 'Out of stock'
    };
  }

  function getVariantViewModel(variant, options) {
    const state = variant || {};
    const config = options || {};
    const retailPriceCents = normalizeCents(state.price);
    const compareAtCents = normalizeCents(state.compare_at_price);
    const dealerPercentage = normalizePercentage(config.dealerDiscountPercentage);
    const addonTotalCents = normalizeCents(config.addonTotalCents);
    const basePriceCents = dealerPercentage > 0
      ? Math.round(retailPriceCents * (100 - dealerPercentage) / 100)
      : retailPriceCents;
    const isMember = config.isApexMember === true
      && normalizePercentage(config.apexDiscountPercentage) > 0;
    const pricing = {
      basePriceCents,
      currentPriceCents: basePriceCents,
      isMember
    };

    if (isMember) {
      const memberPricing = calculateMemberPriceCents(
        basePriceCents,
        config.apexDiscountPercentage
      );
      pricing.currentPriceCents = memberPricing.priceCents;
      pricing.memberPriceCents = memberPricing.priceCents;
      pricing.originalPriceCents = basePriceCents;
      pricing.savingsCents = memberPricing.savingsCents;
    } else if (dealerPercentage > 0) {
      pricing.originalPriceCents = retailPriceCents;
    } else if (compareAtCents > basePriceCents) {
      pricing.originalPriceCents = compareAtCents;
    }

    if (addonTotalCents > 0) {
      pricing.addonTotalCents = addonTotalCents;
      pricing.bundleTotalCents = pricing.currentPriceCents + addonTotalCents;
    }

    const purchaseState = getPurchaseState(state);
    const purchase = {
      disabled: !purchaseState.purchasable,
      text: purchaseState.label
    };

    const featuredMedia = state.featured_media || null;
    return {
      featuredMediaId: state.featuredMediaId
        || state.featured_media_id
        || (featuredMedia && featuredMedia.id)
        || null,
      pricing,
      purchase,
      inventory: purchaseState.inventory,
      inventoryCompact: purchaseState.inventoryCompact || purchaseState.inventory,
      sku: String(state.sku || '')
    };
  }

  function formatMoneyCents(cents, moneyFormat) {
    const format = typeof moneyFormat === 'string' && moneyFormat.includes('{{')
      ? moneyFormat
      : '{{amount}}';
    const placeholder = format.match(/\{\{\s*(amount(?:_[a-z_]+)?)\s*\}\}/i);
    const token = placeholder ? placeholder[1].toLowerCase() : 'amount';
    const formats = {
      amount: { decimals: 2, decimal: '.', thousands: ',' },
      amount_no_decimals: { decimals: 0, decimal: '.', thousands: ',' },
      amount_no_decimals_with_comma_separator: {
        decimals: 0,
        decimal: ',',
        thousands: '.'
      },
      amount_no_decimals_with_space_separator: {
        decimals: 0,
        decimal: ',',
        thousands: ' '
      },
      amount_with_apostrophe_separator: {
        decimals: 2,
        decimal: '.',
        thousands: "'"
      },
      amount_with_comma_separator: { decimals: 2, decimal: ',', thousands: '.' },
      amount_with_space_separator: { decimals: 2, decimal: ',', thousands: ' ' },
      amount_with_period_and_space_separator: {
        decimals: 2,
        decimal: '.',
        thousands: ' '
      }
    };
    const options = formats[token] || formats.amount;
    const fixed = (normalizeCents(cents) / 100).toFixed(options.decimals);
    const parts = fixed.split('.');
    const whole = parts[0].replace(
      /\B(?=(\d{3})+(?!\d))/g,
      options.thousands
    );
    const amount = options.decimals > 0
      ? `${whole}${options.decimal}${parts[1]}`
      : whole;
    return placeholder ? format.replace(placeholder[0], amount) : amount;
  }

  function getAddonTotalCents(root) {
    return Array.from(root.querySelectorAll('.addon-checkbox:checked'))
      .reduce((total, checkbox) => {
        return total + normalizeCents(checkbox.getAttribute('data-price'));
      }, 0);
  }

  /*
    The two hidden line properties that let the team answer, after the fact,
    what we told this shopper about this part for their car. Underscore-prefixed
    so they reach the order in the admin without rendering at checkout.

    The status comes off the fitment block, which renderFitment stamps with the
    presentation it is currently displaying, so a wheel reading "Bolts to your
    2020 Civic Si" records that rather than a chassis-row verdict it never had.
  */
  /*
    Reached through the root's own document, never a global. This module is
    driven with injected windowRef/documentRef and is unit-tested in Node,
    where `window` does not exist; the fixtures also hand in roots with no
    ownerDocument at all, so every step is guarded and the whole thing simply
    declines rather than throwing inside the add-to-cart path.
  */
  function fitmentApiFor(root) {
    const doc = root && root.ownerDocument ? root.ownerDocument : null;
    const view = doc && doc.defaultView ? doc.defaultView : null;
    const api = view && view.SaikoSpeedFitment ? view.SaikoSpeedFitment : null;
    if (!api || typeof api.orderProperties !== 'function') return null;
    return { api, doc };
  }

  function buildFitmentProperties(root) {
    const context = fitmentApiFor(root);
    if (!context) return null;

    const block = root.querySelector('[data-pdp-fitment]');
    const status = block && block.dataset ? String(block.dataset.fitmentStatus || '') : '';
    // 'loading' means the client has not resolved yet; recording it would be
    // a claim about nothing.
    const settled = status && status !== 'loading' ? status : '';

    return context.api.orderProperties({
      vehicle: context.api.parseVehicleCookie(context.doc.cookie),
      status: settled
    });
  }

  function buildCartItems(root, form) {
    const idInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    // The payload is built by hand rather than serialized from the form, so a
    // selling plan has to be read across explicitly. Without it a
    // subscription-only product adds to cart as a one-time purchase.
    const sellingPlanInput = form.querySelector('[name="selling_plan"]');
    const sellingPlan = sellingPlanInput
      ? String(sellingPlanInput.value || '').trim()
      : '';
    const mainVariantId = idInput ? String(idInput.value || '').trim() : '';
    if (!mainVariantId) return [];

    const quantity = Math.max(1, Number.parseInt(
      quantityInput ? quantityInput.value : '1',
      10
    ) || 1);
    const addOns = Array.from(root.querySelectorAll('.addon-checkbox:checked'))
      .map((checkbox) => {
        const id = String(checkbox.getAttribute('data-variant-id') || '').trim();
        const addOnQuantity = Math.max(1, Number.parseInt(
          checkbox.getAttribute('data-quantity') || '1',
          10
        ) || 1);
        return id ? { id, quantity: addOnQuantity } : null;
      })
      .filter(Boolean);

    /*
      The fitment record that travels to the order, for the guarantee.

      Read from the live fitment block rather than recomputed, so it states what
      the shopper was actually shown at the moment they committed, including the
      wheel and fastener spec verdicts the state machine does not produce. Same
      reason the selling plan is read across by hand above: this payload is
      built by hand, so a hidden input alone would be inert.

      The add-ons carry it too. They are separate lines on the order and each
      one is a part the guarantee applies to.
    */
    const fitmentProperties = buildFitmentProperties(root);

    const mainItem = { id: mainVariantId, quantity };
    if (sellingPlan) mainItem.selling_plan = sellingPlan;
    if (fitmentProperties) mainItem.properties = fitmentProperties;

    /*
      Add-ons record the vehicle but NOT the main product's verdict. They are
      different parts on their own lines, and this page never resolved a
      verdict for them against the saved vehicle, so copying the main product's
      would be a claim about something we did not check.
    */
    const addOnContext = fitmentApiFor(root);
    const addOnProperties = addOnContext
      ? addOnContext.api.orderProperties({
        vehicle: addOnContext.api.parseVehicleCookie(addOnContext.doc.cookie),
        fallbackVerdict: 'Not shown at add'
      })
      : null;
    if (addOnProperties) {
      addOns.forEach((addOn) => { addOn.properties = addOnProperties; });
    }

    return [mainItem].concat(addOns);
  }

  async function openMiniCart(documentRef, windowRef, opener, isCurrent) {
    const canContinue = typeof isCurrent === 'function' ? isCurrent : () => true;
    if (!canContinue()) return false;
    const miniCart = documentRef.querySelector('mini-cart') || windowRef.miniCart;
    if (miniCart) {
      try {
        if (typeof miniCart.updateSectionContents === 'function') {
          await miniCart.updateSectionContents();
        }
        if (!canContinue()) return false;
        if (typeof miniCart.open === 'function') {
          miniCart.open(opener);
          return true;
        }
      } catch (error) {
        // Preserve the theme event fallback when its mini-cart API is unavailable.
      }
    }

    if (!canContinue()) return false;
    documentRef.dispatchEvent(new windowRef.CustomEvent('open-mini-cart', {
      detail: { opener }
    }));
    return false;
  }

  function renderPurchaseActionState(root) {
    const form = root.querySelector('[data-pdp-product-form]');
    const canonicalButton = form && typeof form.querySelector === 'function'
      ? form.querySelector('[type="submit"]')
      : root.querySelector('form.product-form .add-to-cart');
    const stickyButton = root.querySelector('[data-pdp-sticky-submit]');
    const submitting = root.dataset && root.dataset.purchaseSubmitting === 'true';
    const currentLabel = root.dataset && root.dataset.purchaseLabel
      ? root.dataset.purchaseLabel
      : canonicalButton && canonicalButton.textContent
        ? canonicalButton.textContent
        : 'Add to cart';
    const purchasable = !root.dataset
      || root.dataset.variantPurchasable !== 'false';
    const label = submitting ? 'Adding\u2026' : currentLabel;
    const disabled = submitting || !purchasable;

    [canonicalButton, stickyButton].filter(Boolean).forEach((button) => {
      button.disabled = disabled;
      button.textContent = label;
    });

    return { disabled, label, submitting };
  }

  async function getCartErrorMessage(response) {
    const fallback = "We couldn't add these items. Try again.";
    if (!response || Number(response.status) !== 422) return fallback;

    try {
      const payload = await response.json();
      const message = payload && (
        typeof payload.description === 'string'
          ? payload.description
          : typeof payload.message === 'string'
            ? payload.message
            : ''
      );
      return message && message.trim() ? message.trim() : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function initializePurchaseForm(root, documentRef, windowRef) {
    const form = root.querySelector('[data-pdp-product-form]');
    if (!form || form.dataset && form.dataset.purchaseInitialized === 'true') {
      return null;
    }

    const submitButton = form.querySelector('[type="submit"]');
    const status = root.querySelector('[data-pdp-cart-status]');
    const fetchRef = windowRef.fetch
      ? windowRef.fetch.bind(windowRef)
      : fetch.bind(windowRef);
    const AbortControllerRef = windowRef.AbortController
      || (typeof AbortController !== 'undefined' ? AbortController : null);
    let submitting = false;
    let destroyed = false;
    let generation = 0;
    let activeAbortController = null;
    if (root.dataset && root.dataset.purchaseSubmitting === 'true') {
      root.dataset.purchaseSubmitting = 'false';
      renderPurchaseActionState(root);
      if (status) status.textContent = '';
    }

    const onSubmit = async (event) => {
      event.preventDefault();
      if (destroyed || submitting) return;

      const items = buildCartItems(root, form);
      if (!submitButton || items.length === 0) return;
      const requestGeneration = ++generation;
      const isCurrent = () => !destroyed && generation === requestGeneration;
      activeAbortController = AbortControllerRef ? new AbortControllerRef() : null;

      submitting = true;
      if (root.dataset) {
        if (!root.dataset.purchaseLabel) {
          root.dataset.purchaseLabel = submitButton.textContent || 'Add to cart';
        }
        if (!root.dataset.variantPurchasable) {
          root.dataset.variantPurchasable = String(!submitButton.disabled);
        }
        root.dataset.purchaseSubmitting = 'true';
      }
      renderPurchaseActionState(root);
      if (status) status.textContent = 'Adding items to cart.';

      try {
        const requestOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        };
        if (activeAbortController) requestOptions.signal = activeAbortController.signal;
        const response = await fetchRef('/cart/add.js', requestOptions);
        if (!isCurrent()) return;
        if (!response.ok) {
          const cartError = new Error('Add to cart failed');
          cartError.customerMessage = await getCartErrorMessage(response);
          if (!isCurrent()) return;
          throw cartError;
        }
        await response.json();
        if (!isCurrent()) return;
        if (status) status.textContent = 'Added to cart.';
        await openMiniCart(documentRef, windowRef, submitButton, isCurrent);
      } catch (error) {
        if (!isCurrent() || error && error.name === 'AbortError') return;
        if (status) {
          status.textContent = error && error.customerMessage
            ? error.customerMessage
            : "We couldn't add these items. Try again.";
        }
      } finally {
        if (!isCurrent()) return;
        activeAbortController = null;
        submitting = false;
        if (root.dataset) root.dataset.purchaseSubmitting = 'false';
        renderPurchaseActionState(root);
      }
    };
    form.addEventListener('submit', onSubmit);

    const quantityInput = form.querySelector('[name="quantity"]');
    const stepButtons = typeof form.querySelectorAll === 'function'
      ? Array.from(form.querySelectorAll('[data-pdp-quantity-step]'))
      : [];
    const syncQuantityStepper = () => {
      if (!quantityInput || stepButtons.length === 0) return;
      const parsed = Number.parseInt(quantityInput.value, 10);
      const current = Number.isFinite(parsed) ? parsed : 1;
      stepButtons.forEach((button) => {
        if (Number(button.dataset.pdpQuantityStep) < 0) button.disabled = current <= 1;
      });
    };
    const onQuantityStep = (event) => {
      if (!quantityInput) return;
      const step = Number(event.currentTarget.dataset.pdpQuantityStep) || 0;
      const parsed = Number.parseInt(quantityInput.value, 10);
      const current = Number.isFinite(parsed) ? parsed : 1;
      quantityInput.value = String(Math.max(1, current + step));
      syncQuantityStepper();
    };
    stepButtons.forEach((button) => button.addEventListener('click', onQuantityStep));
    const watchQuantityInput = stepButtons.length > 0
      && quantityInput
      && typeof quantityInput.addEventListener === 'function';
    if (watchQuantityInput) quantityInput.addEventListener('input', syncQuantityStepper);
    syncQuantityStepper();

    if (form.dataset) form.dataset.purchaseInitialized = 'true';
    return {
      form,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        generation += 1;
        if (activeAbortController) activeAbortController.abort();
        activeAbortController = null;
        submitting = false;
        form.removeEventListener('submit', onSubmit);
        stepButtons.forEach((button) => {
          button.removeEventListener('click', onQuantityStep);
        });
        if (watchQuantityInput) {
          quantityInput.removeEventListener('input', syncQuantityStepper);
        }
        if (form.dataset) delete form.dataset.purchaseInitialized;
      }
    };
  }

  const ETA_API_BASE = 'https://00kdz911a8.execute-api.us-east-2.amazonaws.com/stage1/eta';

  function buildEtaRequestUrl(sku, zip, timestamp) {
    return ETA_API_BASE
      + '?sku=' + encodeURIComponent(String(sku || ''))
      + '&customerzip=' + encodeURIComponent(String(zip || ''))
      + '&ts=' + encodeURIComponent(String(timestamp));
  }

  function initializeEta(root, documentRef, windowRef) {
    const eta = root.querySelector('[data-pdp-eta]');
    if (!eta || eta.dataset.etaInitialized === 'true') return null;

    const display = eta.querySelector('[data-pdp-eta-display]');
    const edit = eta.querySelector('[data-pdp-eta-edit]');
    const line = eta.querySelector('[data-pdp-eta-line]');
    const etaMessage = eta.querySelector('[data-pdp-eta-message]');
    const shippingMessage = eta.querySelector('[data-pdp-shipping-message]');
    const changeHint = eta.querySelector('[data-pdp-eta-change]');
    const contact = eta.querySelector('[data-pdp-eta-contact]');
    const zipInput = eta.querySelector('[data-pdp-postal-code]');
    const updateButton = eta.querySelector('[data-pdp-postal-update]');
    if (
      !display
      || !edit
      || !line
      || !etaMessage
      || !zipInput
      || !updateButton
    ) return null;

    if (typeof windowRef.fetch !== 'function') return null;
    const requestGate = createAbortableLatestRequestGate(windowRef.AbortController);
    const fetchRef = windowRef.fetch.bind(windowRef);
    const storage = windowRef.localStorage;
    const now = windowRef.Date && typeof windowRef.Date.now === 'function'
      ? () => windowRef.Date.now()
      : () => Date.now();
    let etaEnabled = false;

    const isValidZip = (value) => /^\d{5}$/.test(String(value || '').trim());
    const getVariant = () => {
      const productId = String(root.dataset.productId || '');
      const variantId = String(root.dataset.selectedVariantId || '');
      const inventory = windowRef.inventories && windowRef.inventories[productId];
      return inventory && variantId ? inventory[variantId] || null : null;
    };
    const setMode = (mode) => {
      if (!etaEnabled) {
        // Collapse the whole block so no phantom margins remain when the
        // service has nothing to say for this SKU.
        eta.hidden = true;
        display.disabled = true;
        display.hidden = true;
        display.style.display = 'none';
        edit.hidden = true;
        edit.style.display = 'none';
        return;
      }
      eta.hidden = false;
      const editing = mode === 'edit' && etaEnabled;
      display.disabled = false;
      display.hidden = editing;
      display.style.display = editing ? 'none' : 'block';
      edit.hidden = !editing;
      edit.style.display = editing ? 'flex' : 'none';
      if (editing) {
        zipInput.value = String(storage.getItem('savedZipCode') || '').trim();
        if (typeof zipInput.focus === 'function') zipInput.focus();
      }
    };
    const setTriggerAvailable = (available) => {
      etaEnabled = available === true;
      line.style.cursor = etaEnabled ? 'pointer' : 'default';
      setMode('display');
    };
    const setEtaMessage = (message, zip) => {
      const text = message && zip ? `${message} to ${zip}` : message || '';
      etaMessage.textContent = text;
      etaMessage.hidden = !text;
      if (changeHint) changeHint.hidden = !text;
      if (shippingMessage) shippingMessage.textContent = '';
      // No estimate and no open editor means nothing to occupy the block;
      // collapse it so the decision cluster keeps its rhythm.
      const editing = etaEnabled && edit.hidden === false;
      eta.hidden = !etaEnabled || (!text && !editing);
    };
    // A purchasable item the estimate service cannot answer for still gets
    // an honest line: the human path.
    const setContactFallback = (shown) => {
      if (!contact) return;
      contact.hidden = !shown;
      if (shown) eta.hidden = false;
    };
    const normalizeEtaText = (value) => {
      let text = String(value || '').trim().replace(/\.+\s*$/, '').trim();
      if (!text) return '';
      if (/^est\b/i.test(text) || /delivery/i.test(text)) return text;
      return `Est. Delivery ${text}`;
    };
    const update = async () => {
      const request = requestGate.next();
      const variant = getVariant();
      setContactFallback(false);
      const purchase = variant ? getPurchaseState(variant) : { purchasable: false };
      if (!variant || !purchase.purchasable) {
        setTriggerAvailable(false);
        setEtaMessage('');
        // A sold-out variant's lead line invites contact for a backorder
        // or an ETA; the human-path button is that invitation's target.
        if (variant) setContactFallback(true);
        return;
      }

      const quantity = Number(
        typeof variant.quantity === 'number'
          ? variant.quantity
          : variant.inventory_quantity
      );
      const tracked = variant.inventory_management != null;
      const inStock = tracked && quantity > 0;
      const shipsFromManufacturer = !tracked && !(quantity > 0);
      const zip = String(storage.getItem('savedZipCode') || '').trim();
      // In-stock items whose delivery time the service cannot answer get
      // the standard promise line with the merchant's stated window, and
      // keep the ZIP flow so every SKU reads the same. Anything else
      // without an answer gets the human path.
      const fallBack = () => {
        if (inStock && isValidZip(zip)) {
          setTriggerAvailable(true);
          setMode('display');
          setEtaMessage('Delivery estimated in 4-5 business days', zip);
          return;
        }
        if (inStock) {
          setTriggerAvailable(true);
          setEtaMessage('Enter ZIP to calculate delivery ETA.');
          setMode('edit');
          return;
        }
        setTriggerAvailable(false);
        setEtaMessage('');
        setContactFallback(true);
      };

      if (shipsFromManufacturer) {
        // Manufacturer-shipped SKUs keep the ZIP flow for cohesion, but the
        // answer after a ZIP is the human path, not a stated window.
        if (!isValidZip(zip)) {
          setTriggerAvailable(true);
          setEtaMessage('Enter ZIP to calculate delivery ETA.');
          setMode('edit');
          return;
        }
        setTriggerAvailable(false);
        setEtaMessage('');
        setContactFallback(true);
        return;
      }

      if (!variant.sku || !tracked) {
        fallBack();
        return;
      }

      setTriggerAvailable(true);
      if (!isValidZip(zip)) {
        setEtaMessage('Enter ZIP to calculate delivery ETA.');
        setMode('edit');
        return;
      }

      setMode('display');
      try {
        const response = await fetchRef(
          buildEtaRequestUrl(variant.sku, zip, now()),
          { cache: 'no-store', signal: request.signal }
        );
        let data = null;
        try {
          data = await response.json();
        } catch (error) {
          data = null;
        }
        if (!request.isCurrent()) return;
        if (!response.ok) throw new Error('ETA API failed');
        if (data === 'Invalid SKU provided') {
          fallBack();
          return;
        }

        const rawEta = data && data.ETA ? String(data.ETA) : '';
        if (/out\s+of\s+stock/i.test(rawEta) || !rawEta.trim()) {
          fallBack();
          return;
        }
        setEtaMessage(normalizeEtaText(rawEta), zip);
      } catch (error) {
        if (request.isCurrent()) fallBack();
      }
    };

    const onLineClick = () => {
      if (etaEnabled && !display.disabled) setMode('edit');
    };
    const onUpdateClick = () => {
      if (!etaEnabled) return;
      const zip = String(zipInput.value || '').replace(/\D/g, '').slice(0, 5);
      zipInput.value = zip;
      if (!isValidZip(zip)) {
        zipInput.setCustomValidity('Please enter a valid 5-digit ZIP code.');
        if (typeof zipInput.reportValidity === 'function') zipInput.reportValidity();
        return;
      }
      zipInput.setCustomValidity('');
      storage.setItem('savedZipCode', zip);
      update();
    };
    const onZipInput = () => {
      zipInput.value = String(zipInput.value || '').replace(/\D/g, '').slice(0, 5);
      zipInput.setCustomValidity('');
    };
    const onZipKeydown = (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      if (typeof updateButton.click === 'function') updateButton.click();
    };
    line.addEventListener('click', onLineClick);
    updateButton.addEventListener('click', onUpdateClick);
    zipInput.addEventListener('input', onZipInput);
    zipInput.addEventListener('keydown', onZipKeydown);

    root.saikoUpdateEta = update;
    root.saikoClearEta = () => {
      requestGate.invalidate();
      zipInput.setCustomValidity('');
      setTriggerAvailable(false);
      setEtaMessage('');
    };
    setTriggerAvailable(false);
    eta.dataset.etaInitialized = 'true';
    return {
      update,
      destroy() {
        requestGate.invalidate();
        line.removeEventListener('click', onLineClick);
        updateButton.removeEventListener('click', onUpdateClick);
        zipInput.removeEventListener('input', onZipInput);
        zipInput.removeEventListener('keydown', onZipKeydown);
        if (root.saikoUpdateEta === update) delete root.saikoUpdateEta;
        delete root.saikoClearEta;
        delete eta.dataset.etaInitialized;
      }
    };
  }

  function getRelatedProductFitmentLabel(payload, vehicle, fitmentApi) {
    if (!vehicle || !fitmentApi) return 'Recommended add-on';
    try {
      const entries = fitmentApi.parseFitmentEntries(payload);
      const state = fitmentApi.resolveFitmentState(entries, vehicle);
      return state.status === 'confirmed'
        ? 'Confirmed for your vehicle'
        : 'Recommended add-on';
    } catch (error) {
      return 'Recommended add-on';
    }
  }

  function renderRelatedProductFitment(root, fitmentApi, cookieString) {
    if (!fitmentApi || typeof fitmentApi.parseVehicleCookie !== 'function') return;
    const vehicle = fitmentApi.parseVehicleCookie(cookieString);
    root.querySelectorAll('[data-pdp-related-item]').forEach((item) => {
      const payloadNode = item.querySelector('[data-pdp-addon-fitment]');
      const label = item.querySelector('[data-pdp-addon-fitment-label]');
      if (!payloadNode || !label) return;
      let payload = [];
      try {
        payload = JSON.parse(payloadNode.textContent);
      } catch (error) {
        payload = [];
      }
      label.textContent = getRelatedProductFitmentLabel(payload, vehicle, fitmentApi);
    });
  }

  function initializeGallery(root, documentRef, windowRef) {
    if (!root) return null;
    const gallery = root.querySelector('[data-pdp-gallery]')
      || root.querySelector('#productGallery');
    if (!gallery || gallery.dataset.galleryInitialized === 'true') return null;

    const items = Array.from(gallery.querySelectorAll('.gallery-main__item'));
    if (items.length === 0) return null;

    const viewport = gallery.querySelector('.gallery-main');
    const previousButton = gallery.querySelector('.gallery-nav.prev');
    const nextButton = gallery.querySelector('.gallery-nav.next');
    const dotsWrap = gallery.querySelector('.gallery-dots');
    const galleryId = gallery.getAttribute('data-gallery-id') || 'ProductGallery';
    const listeners = [];
    const pendingFrames = new Set();
    let activeIndex = items.findIndex((item) => item.classList.contains('active'));
    let scrollFrame = null;
    let releaseFrame = null;
    let temporaryScrollBehavior = null;
    let programmaticScrollTimer = null;
    let isProgrammaticScroll = false;
    let touchStartX = null;
    let touchStartY = null;
    let destroyed = false;
    if (activeIndex < 0) activeIndex = 0;

    const addListener = (target, name, handler, options) => {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(name, handler, options);
      listeners.push([target, name, handler, options]);
    };
    const requestFrame = (callback) => {
      if (typeof windowRef.requestAnimationFrame !== 'function') {
        if (!destroyed) callback();
        return null;
      }
      let frameId = null;
      frameId = windowRef.requestAnimationFrame(() => {
        pendingFrames.delete(frameId);
        if (!destroyed) callback();
      });
      pendingFrames.add(frameId);
      return frameId;
    };
    const cancelFrame = (frameId) => {
      if (frameId === null || frameId === undefined) return;
      if (typeof windowRef.cancelAnimationFrame === 'function') {
        windowRef.cancelAnimationFrame(frameId);
      }
      pendingFrames.delete(frameId);
    };
    const clearProgrammaticTimer = () => {
      if (programmaticScrollTimer === null) return;
      windowRef.clearTimeout(programmaticScrollTimer);
      programmaticScrollTimer = null;
    };
    const restoreTemporaryScrollBehavior = () => {
      if (!viewport || temporaryScrollBehavior === null) return;
      viewport.style.scrollBehavior = temporaryScrollBehavior;
      temporaryScrollBehavior = null;
    };

    const pauseGalleryMedia = () => {
      if (typeof windowRef.pauseAllMedia === 'function') {
        try {
          windowRef.pauseAllMedia();
          return;
        } catch (error) {
          // Fall through to the section-scoped media controls.
        }
      }
      gallery.querySelectorAll('video').forEach((video) => {
        if (typeof video.pause === 'function') video.pause();
      });
      gallery.querySelectorAll('.js-youtube').forEach((iframe) => {
        if (!iframe.contentWindow) return;
        iframe.contentWindow.postMessage(
          '{"event":"command","func":"pauseVideo","args":""}',
          '*'
        );
      });
      gallery.querySelectorAll('.js-vimeo').forEach((iframe) => {
        if (iframe.contentWindow) iframe.contentWindow.postMessage('{"method":"pause"}', '*');
      });
      gallery.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI && typeof model.modelViewerUI.pause === 'function') {
          model.modelViewerUI.pause();
          return;
        }
        const modelViewer = model.querySelector('model-viewer');
        if (modelViewer && typeof modelViewer.pause === 'function') modelViewer.pause();
      });
    };

    const closestVisibleIndex = () => {
      if (!viewport || viewport.clientWidth === 0) return activeIndex;
      const viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
      let nextIndex = activeIndex;
      let smallestDistance = Number.POSITIVE_INFINITY;
      items.forEach((item, index) => {
        const distance = Math.abs(
          item.offsetLeft + item.offsetWidth / 2 - viewportCenter
        );
        if (distance < smallestDistance) {
          smallestDistance = distance;
          nextIndex = index;
        }
      });
      return nextIndex;
    };

    let setActive;
    const syncActiveFromScroll = () => {
      scrollFrame = null;
      if (destroyed || isProgrammaticScroll) return;
      const visibleIndex = closestVisibleIndex();
      if (visibleIndex !== activeIndex) {
        setActive(visibleIndex, { focusDot: false, scrollMedia: false });
      }
    };
    const scheduleScrollSync = () => {
      if (destroyed || isProgrammaticScroll || scrollFrame !== null) return;
      scrollFrame = requestFrame(syncActiveFromScroll);
    };
    const releaseProgrammaticScroll = () => {
      if (destroyed || !isProgrammaticScroll) return;
      isProgrammaticScroll = false;
      clearProgrammaticTimer();
      scheduleScrollSync();
    };

    setActive = (index, options = {}) => {
      if (destroyed) return false;
      const nextIndex = (index + items.length) % items.length;
      if (nextIndex !== activeIndex) pauseGalleryMedia();

      items.forEach((item, itemIndex) => {
        const isActive = itemIndex === nextIndex;
        item.classList.toggle('active', isActive);
        item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) item.removeAttribute('inert');
        else item.setAttribute('inert', '');
      });

      const dots = dotsWrap
        ? Array.from(dotsWrap.querySelectorAll('button'))
        : [];
      dots.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === nextIndex;
        button.classList.toggle('active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
        button.tabIndex = isActive ? 0 : -1;
      });
      activeIndex = nextIndex;

      if (options.scrollMedia === true && viewport) {
        const activeItem = items[activeIndex];
        const canScroll = viewport.scrollWidth > viewport.clientWidth + 1;
        const needsScroll = activeItem
          && Math.abs(viewport.scrollLeft - activeItem.offsetLeft) > 1;
        if (canScroll && needsScroll) {
          clearProgrammaticTimer();
          if (releaseFrame !== null) {
            cancelFrame(releaseFrame);
            releaseFrame = null;
            restoreTemporaryScrollBehavior();
          }
          isProgrammaticScroll = true;
          if (options.instant === true) {
            const previousScrollBehavior = viewport.style.scrollBehavior;
            temporaryScrollBehavior = previousScrollBehavior;
            viewport.style.scrollBehavior = 'auto';
            viewport.scrollLeft = activeItem.offsetLeft;
            releaseFrame = requestFrame(() => {
              releaseFrame = null;
              restoreTemporaryScrollBehavior();
              releaseProgrammaticScroll();
            });
          } else {
            const reduceMotion = typeof windowRef.matchMedia === 'function'
              && windowRef.matchMedia('(prefers-reduced-motion: reduce)').matches;
            viewport.scrollTo({
              left: activeItem.offsetLeft,
              behavior: reduceMotion ? 'auto' : 'smooth'
            });
            programmaticScrollTimer = windowRef.setTimeout(
              releaseProgrammaticScroll,
              500
            );
          }
        }
      }

      if (options.focusDot === true && dots[activeIndex]) dots[activeIndex].focus();
      return true;
    };

    if (dotsWrap && dotsWrap.children.length === 0) {
      dotsWrap.setAttribute('role', 'tablist');
      items.forEach((item, index) => {
        const button = documentRef.createElement('button');
        const tabId = `${galleryId}-tab-generated-${index}`;
        button.type = 'button';
        button.id = tabId;
        button.className = `gallery-dot${index === activeIndex ? ' active' : ''}`;
        button.setAttribute('role', 'tab');
        button.setAttribute('data-gallery-index', String(index));
        button.setAttribute('aria-label', `View image ${index + 1} of ${items.length}`);
        if (item.id) {
          button.setAttribute('aria-controls', item.id);
          item.setAttribute('aria-labelledby', tabId);
        }
        dotsWrap.appendChild(button);
      });
    }

    if (dotsWrap) {
      Array.from(dotsWrap.querySelectorAll('button')).forEach((button, index) => {
        const onClick = () => {
          const requestedIndex = Number(button.getAttribute('data-gallery-index'));
          setActive(Number.isNaN(requestedIndex) ? index : requestedIndex, {
            focusDot: false,
            scrollMedia: true
          });
        };
        const onKeydown = (event) => {
          const dataIndex = Number(button.getAttribute('data-gallery-index'));
          const currentIndex = Number.isNaN(dataIndex) ? index : dataIndex;
          let requestedIndex = currentIndex;
          if (event.key === 'ArrowRight') requestedIndex += 1;
          else if (event.key === 'ArrowLeft') requestedIndex -= 1;
          else if (event.key === 'Home') requestedIndex = 0;
          else if (event.key === 'End') requestedIndex = items.length - 1;
          else return;
          event.preventDefault();
          setActive(requestedIndex, { focusDot: true, scrollMedia: true });
        };
        addListener(button, 'click', onClick);
        addListener(button, 'keydown', onKeydown);
      });
    }

    const onPrevious = () => {
      setActive(activeIndex - 1, { focusDot: false, scrollMedia: true });
    };
    const onNext = () => {
      setActive(activeIndex + 1, { focusDot: false, scrollMedia: true });
    };
    addListener(previousButton, 'click', onPrevious);
    addListener(nextButton, 'click', onNext);
    addListener(viewport, 'scroll', scheduleScrollSync, { passive: true });
    addListener(viewport, 'scrollend', releaseProgrammaticScroll);

    const onTouchStart = (event) => {
      if (!event.touches || event.touches.length !== 1) return;
      touchStartX = event.touches[0].clientX;
      touchStartY = event.touches[0].clientY;
    };
    const onTouchEnd = (event) => {
      if (touchStartX === null || touchStartY === null) return;
      const touch = event.changedTouches && event.changedTouches[0];
      if (!touch) return;
      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;
      touchStartX = null;
      touchStartY = null;
      if (Math.abs(deltaX) >= 35 && Math.abs(deltaX) >= Math.abs(deltaY)) {
        scheduleScrollSync();
      }
    };
    addListener(gallery, 'touchstart', onTouchStart, { passive: true });
    addListener(gallery, 'touchend', onTouchEnd, { passive: true });

    gallery.dataset.galleryInitialized = 'true';
    setActive(activeIndex, { focusDot: false, scrollMedia: true, instant: true });
    return {
      setActive,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        listeners.forEach(([target, name, handler, options]) => {
          target.removeEventListener(name, handler, options);
        });
        clearProgrammaticTimer();
        restoreTemporaryScrollBehavior();
        pendingFrames.forEach((frameId) => {
          if (typeof windowRef.cancelAnimationFrame === 'function') {
            windowRef.cancelAnimationFrame(frameId);
          }
        });
        pendingFrames.clear();
        scrollFrame = null;
        releaseFrame = null;
        delete gallery.dataset.galleryInitialized;
      }
    };
  }

  function isElementRendered(element, windowRef) {
    const visited = new Set();
    const getRenderedParent = (current) => {
      if (current.parentElement) return current.parentElement;
      if (typeof current.getRootNode !== 'function') return null;
      const rootNode = current.getRootNode();
      return rootNode && rootNode.host && rootNode.host !== current
        ? rootNode.host
        : null;
    };

    for (
      let current = element;
      current && !visited.has(current);
      current = getRenderedParent(current)
    ) {
      visited.add(current);
      if (
        current.hidden === true
        || (
          typeof current.hasAttribute === 'function'
          && current.hasAttribute('hidden')
        )
        || (
          typeof current.getAttribute === 'function'
          && current.getAttribute('aria-hidden') === 'true'
        )
      ) return false;

      if (windowRef && typeof windowRef.getComputedStyle === 'function') {
        const style = windowRef.getComputedStyle(current);
        if (style && (style.display === 'none' || style.visibility === 'hidden')) {
          return false;
        }
      }
    }
    return true;
  }

  function isOpenPurchaseOverlay(element) {
    const tagName = String(element.tagName || '').toLowerCase();
    const getAttribute = typeof element.getAttribute === 'function'
      ? (name) => element.getAttribute(name)
      : () => null;
    const hasAttribute = typeof element.hasAttribute === 'function'
      ? (name) => element.hasAttribute(name)
      : () => false;
    const hasClass = element.classList && typeof element.classList.contains === 'function'
      ? (name) => element.classList.contains(name)
      : () => false;

    if (tagName === 'shopify-chat') return hasAttribute('open');
    if (tagName === 'dialog') return hasAttribute('open') || element.open === true;
    if (getAttribute('role') === 'dialog') {
      const isDropdownModal = tagName.endsWith('dropdown-modal')
        || hasClass('dropdown-modal');
      return !isDropdownModal || hasAttribute('open');
    }
    if (tagName === 'mini-cart') return hasAttribute('open');

    const isCartDrawer = hasAttribute('data-cart-drawer') || hasClass('cart-drawer');
    return isCartDrawer && (
      hasAttribute('open')
      || getAttribute('aria-hidden') === 'false'
      || hasClass('is-open')
      || hasClass('active')
    );
  }

  function hasVisibleViewportRect(element, windowRef) {
    if (typeof element.getBoundingClientRect !== 'function') return false;
    const rect = element.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return false;
    const viewportWidth = Number(windowRef && windowRef.innerWidth);
    const viewportHeight = Number(windowRef && windowRef.innerHeight);
    return rect.right > 0
      && rect.bottom > 0
      && (!Number.isFinite(viewportWidth) || viewportWidth <= 0 || rect.left < viewportWidth)
      && (!Number.isFinite(viewportHeight) || viewportHeight <= 0 || rect.top < viewportHeight);
  }

  function hasBlockingPurchaseOverlay(documentRef, windowRef) {
    if (typeof documentRef.querySelectorAll !== 'function') return false;
    const candidates = documentRef.querySelectorAll(
      'dialog, [role="dialog"], mini-cart, shopify-chat, [data-cart-drawer], .cart-drawer'
    );
    return Array.from(candidates).some((element) => {
      if (!isOpenPurchaseOverlay(element) || !isElementRendered(element, windowRef)) return false;
      return String(element.tagName || '').toLowerCase() !== 'shopify-chat'
        || hasVisibleViewportRect(element, windowRef);
    });
  }

  function initializeStickyPurchase(root, documentRef, windowRef) {
    const primaryActions = root.querySelector('[data-pdp-primary-actions]');
    const sticky = root.querySelector('[data-pdp-sticky-purchase]');
    const stickyButton = root.querySelector('[data-pdp-sticky-submit]');
    const form = root.querySelector('form[data-pdp-product-form]');
    if (
      !primaryActions
      || !sticky
      || !stickyButton
      || !form
      || typeof windowRef.IntersectionObserver !== 'function'
    ) return null;

    const media = windowRef.matchMedia('(max-width: 749px)');
    let primaryPassed = false;
    let mutationObserver = null;
    let observedMutationTargets = [];
    let onResize = null;
    let destroyed = false;
    const setAttribute = (name, value) => {
      if (
        typeof sticky.getAttribute === 'function'
        && sticky.getAttribute(name) === value
      ) return;
      sticky.setAttribute(name, value);
    };
    const setInert = (inert) => {
      if (inert) {
        if (
          typeof sticky.hasAttribute !== 'function'
          || !sticky.hasAttribute('inert')
        ) sticky.setAttribute('inert', '');
      } else if (
        typeof sticky.hasAttribute !== 'function'
        || sticky.hasAttribute('inert')
      ) {
        sticky.removeAttribute('inert');
      }
    };
    const updateTopClearance = () => {
      if (!root.style || typeof root.style.setProperty !== 'function') return;
      if (typeof documentRef.querySelectorAll !== 'function') return;
      // The header group also holds the announcement bar, which scrolls away;
      // the stuck chrome is whichever group section still has the lowest
      // on-screen bottom edge.
      let top = 0;
      documentRef.querySelectorAll('.shopify-section-group-header-group').forEach(
        (headerSection) => {
          if (typeof headerSection.getBoundingClientRect !== 'function') return;
          const bottom = Number(headerSection.getBoundingClientRect().bottom);
          if (Number.isFinite(bottom) && bottom > top) top = Math.round(bottom);
        }
      );
      const propertyValue = `${top}px`;
      const currentValue = typeof root.style.getPropertyValue === 'function'
        ? root.style.getPropertyValue('--pdp-sticky-top')
        : '';
      if (currentValue !== propertyValue) {
        root.style.setProperty('--pdp-sticky-top', propertyValue);
      }
    };
    // Suppression uses opacity + pointer-events rather than visibility or
    // display: the host must stay "rendered" so an opening chat window is
    // still recognized as a blocking overlay and can stand the bar down.
    let suppressedChatHost = null;
    const setChatSuppressed = (host, suppressed) => {
      if (suppressed) {
        if (!host || !host.style) return;
        if (host.style.opacity !== '0') {
          host.style.opacity = '0';
          host.style.pointerEvents = 'none';
        }
        suppressedChatHost = host;
        return;
      }
      const restoreTarget = host && host.style ? host : suppressedChatHost;
      if (restoreTarget && restoreTarget.style && restoreTarget.style.opacity === '0') {
        restoreTarget.style.opacity = '';
        restoreTarget.style.pointerEvents = '';
      }
      suppressedChatHost = null;
    };
    const refresh = () => {
      if (destroyed) return false;
      const chatResolution = resolveShopifyChat(documentRef, windowRef);
      syncMutationTargets(chatResolution.shadowRoots);
      // Bottom-bar clearance (consent surfaces) only applies at mobile
      // widths; the top bar instead measures the stuck header's bottom edge.
      // The visibility contract itself is width-independent - CSS decides
      // where and how the mirror renders.
      if (media.matches) {
        updateFixedClearance(root, documentRef, windowRef);
      } else {
        updateTopClearance();
      }
      const shouldShow = primaryPassed
        && !hasBlockingPurchaseOverlay(documentRef, windowRef);
      // The mobile bar owns the bottom edge: the Inbox launcher hides while
      // the bar is interactive (the bar carries its own Ask a build expert
      // trigger) and returns as soon as the bar stands down - including
      // when the chat itself opens and suppresses the bar.
      setChatSuppressed(chatResolution.chat, media.matches && shouldShow);
      if (sticky.hidden) sticky.hidden = false;
      sticky.dataset.visible = shouldShow ? 'true' : 'false';
      setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      setInert(!shouldShow);
      return shouldShow;
    };
    let refreshQueued = false;
    const scheduleRefresh = () => {
      if (destroyed || refreshQueued) return;
      if (typeof windowRef.requestAnimationFrame !== 'function') {
        refresh();
        return;
      }
      refreshQueued = true;
      windowRef.requestAnimationFrame(() => {
        refreshQueued = false;
        refresh();
      });
    };

    const observer = new windowRef.IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const bottom = entry && entry.boundingClientRect
        ? Number(entry.boundingClientRect.bottom)
        : Number.NaN;
      primaryPassed = Boolean(
        entry
        && entry.isIntersecting !== true
        && Number.isFinite(bottom)
        && bottom <= 0
      );
      refresh();
    });
    observer.observe(primaryActions);

    const onStickyClick = (event) => {
      event.preventDefault();
      if (stickyButton.disabled) return;
      const submitButton = form.querySelector('[type="submit"]');
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton);
      else if (submitButton && typeof submitButton.click === 'function') submitButton.click();
    };
    stickyButton.addEventListener('click', onStickyClick);

    if (typeof media.addEventListener === 'function') media.addEventListener('change', refresh);
    else if (typeof media.addListener === 'function') media.addListener(refresh);

    const mutationOptions = {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'hidden', 'inert', 'open', 'style'],
      childList: true,
      subtree: true
    };
    function syncMutationTargets(shadowRoots) {
      if (!mutationObserver || !documentRef.body) return;
      const targets = [documentRef.body, ...shadowRoots];
      const unchanged = targets.length === observedMutationTargets.length
        && targets.every((target) => observedMutationTargets.includes(target));
      if (unchanged) return;
      if (observedMutationTargets.length > 0) mutationObserver.disconnect();
      targets.forEach((target) => mutationObserver.observe(target, mutationOptions));
      observedMutationTargets = targets;
    }

    if (typeof windowRef.MutationObserver === 'function' && documentRef.body) {
      mutationObserver = new windowRef.MutationObserver(scheduleRefresh);
    }
    if (typeof windowRef.addEventListener === 'function') {
      onResize = scheduleRefresh;
      windowRef.addEventListener('resize', onResize);
    }

    refresh();
    return {
      refresh,
      destroy() {
        if (destroyed) return;
        destroyed = true;
        setChatSuppressed(null, false);
        observer.disconnect();
        if (mutationObserver) mutationObserver.disconnect();
        observedMutationTargets = [];
        stickyButton.removeEventListener('click', onStickyClick);
        if (onResize) windowRef.removeEventListener('resize', onResize);
        if (typeof media.removeEventListener === 'function') {
          media.removeEventListener('change', refresh);
        } else if (typeof media.removeListener === 'function') {
          media.removeListener(refresh);
        }
      }
    };
  }

  function calculateFixedClearance(rects, viewportHeight, gap) {
    const spacing = Number.isFinite(Number(gap)) ? Number(gap) : 8;
    return (rects || []).reduce((clearance, rect) => {
      const visible = rect
        && rect.width > 0
        && rect.height > 0
        && rect.bottom > 0
        && rect.top < viewportHeight;
      if (!visible) return clearance;
      return Math.max(clearance, Math.max(0, viewportHeight - rect.top) + spacing);
    }, 0);
  }

  function inspectOpenShadowTree(
    host,
    selector,
    matchPredicate,
    maxDepth = 6,
    maxRoots = 32,
    maxElements = 256
  ) {
    if (!host || !host.shadowRoot) return { match: null, shadowRoots: [] };
    const queue = [{ depth: 0, root: host.shadowRoot }];
    const visited = new Set();
    const shadowRoots = [];
    let match = null;
    let elementVisits = 0;

    while (
      queue.length > 0
      && shadowRoots.length < maxRoots
      && elementVisits < maxElements
    ) {
      const { depth, root: shadowRoot } = queue.shift();
      if (!shadowRoot || visited.has(shadowRoot)) continue;
      visited.add(shadowRoot);
      shadowRoots.push(shadowRoot);
      const pendingElements = [];
      let element = shadowRoot.firstElementChild || null;
      while (element && elementVisits < maxElements) {
        elementVisits += 1;
        if (
          !match
          && typeof element.matches === 'function'
          && element.matches(selector)
          && (!matchPredicate || matchPredicate(element))
        ) {
          match = element;
        }
        if (
          depth < maxDepth
          && element.shadowRoot
          && !visited.has(element.shadowRoot)
          && shadowRoots.length + queue.length < maxRoots
        ) {
          queue.push({ depth: depth + 1, root: element.shadowRoot });
        }
        if (element.nextElementSibling) pendingElements.push(element.nextElementSibling);
        element = element.firstElementChild || pendingElements.pop() || null;
      }
    }

    return { elementVisits, match, shadowRoots };
  }

  function resolveShopifyChat(documentRef, windowRef) {
    const chat = typeof documentRef.querySelector === 'function'
      ? documentRef.querySelector('shopify-chat')
      : null;
    const inspection = inspectOpenShadowTree(
      chat,
      '[part~="activator"], button[aria-label*="Chat" i]',
      (element) => isElementRendered(element, windowRef)
    );
    return {
      activator: inspection.match,
      chat,
      shadowRoots: inspection.shadowRoots
    };
  }

  function updateFixedClearance(root, documentRef, windowRef) {
    if (!root.style || typeof root.style.setProperty !== 'function') return 0;
    // Consent surfaces only: the Inbox launcher no longer needs clearance
    // because the mobile bar hides it outright while interactive.
    const elements = [];
    if (typeof documentRef.querySelectorAll === 'function') {
      documentRef.querySelectorAll(
        '#shopify-pc__banner, .shopify-pc__banner, '
        + '[data-testid*="consent" i], [class*="consent" i][role="dialog"]'
      ).forEach((element) => elements.push(element));
    }
    const rects = elements
      .filter((element) => typeof element.getBoundingClientRect === 'function')
      .map((element) => element.getBoundingClientRect());
    const clearance = calculateFixedClearance(rects, windowRef.innerHeight || 0, 8);
    const propertyName = '--pdp-fixed-clearance';
    const propertyValue = `${clearance}px`;
    const currentValue = typeof root.style.getPropertyValue === 'function'
      ? root.style.getPropertyValue(propertyName)
      : '';
    if (currentValue !== propertyValue) {
      root.style.setProperty(propertyName, propertyValue);
    }
    return clearance;
  }

  function findVariantByOptions(inventory, selectedOptions) {
    const records = inventory ? Object.values(inventory) : [];
    return records.find((variant) => {
      return Array.isArray(variant.options)
        && variant.options.length >= selectedOptions.length
        && selectedOptions.every((value, index) => variant.options[index] === value);
    }) || null;
  }

  function getOptionValueAvailability(records, optionIndex, selectedOptions) {
    const variants = Array.isArray(records) ? records : [];
    const selections = Array.isArray(selectedOptions) ? selectedOptions : [];
    const availability = {};
    const values = new Set();
    variants.forEach((variant) => {
      if (!Array.isArray(variant.options)) return;
      const value = variant.options[optionIndex];
      if (typeof value === 'string' && value !== '') values.add(value);
    });
    values.forEach((value) => {
      availability[value] = variants.some((variant) => {
        if (!Array.isArray(variant.options)) return false;
        if (variant.options[optionIndex] !== value) return false;
        return selections.every((selected, index) => {
          if (index === optionIndex) return true;
          if (selected == null || selected === '') return true;
          return variant.options[index] === selected;
        });
      });
    });
    return availability;
  }

  function renderVariantState(root, variant, windowRef) {
    if (!root) return null;
    if (!variant) {
      const idInput = root.querySelector('form.product-form input[name="id"]');
      const pricingRoot = root.querySelector('[data-pdp-price]');
      const sku = root.querySelector('.product-single__sku');
      const availability = root.querySelector('[data-pdp-availability]');
      const stickyInventory = root.querySelector('[data-pdp-sticky-inventory]');
      const stickyPrice = root.querySelector('[data-pdp-sticky-price]');
      const stickyContext = root.querySelector('[data-pdp-sticky-context]');
      const affirmElement = root.querySelector('.affirm-as-low-as');
      root.dataset.selectedVariantId = '';
      root.dataset.selectedMediaId = '';
      root.dataset.variantAvailable = 'false';
      root.dataset.variantInventoryPolicy = '';
      root.dataset.variantPurchasable = 'false';
      root.dataset.variantQuantity = '';
      root.dataset.variantState = 'unavailable';
      root.dataset.purchaseLabel = 'Choose an option';
      if (typeof root.saikoClearEta === 'function') root.saikoClearEta();
      if (pricingRoot) {
        pricingRoot.dataset.variantState = 'unavailable';
        pricingRoot.hidden = true;
      }
      if (idInput) idInput.value = '';
      if (sku) {
        sku.textContent = '';
        sku.hidden = true;
      }
      root.querySelectorAll(
        '[data-public-price], [data-member-price], [data-original-price], [data-member-savings]'
      ).forEach((node) => {
        node.textContent = '';
        node.hidden = true;
      });
      const win = windowRef || {};
      const productId = String(root.dataset.productId || '');
      const inventory = win.inventories && win.inventories[productId];
      const records = inventory ? Object.values(inventory) : [];
      if (records.length > 0 && pricingRoot) {
        const config = {
          apexDiscountPercentage: pricingRoot.dataset
            ? pricingRoot.dataset.apexDiscount
            : undefined,
          dealerDiscountPercentage: pricingRoot.dataset
            ? pricingRoot.dataset.dealerDiscount
            : undefined,
          isApexMember: pricingRoot.dataset
            ? pricingRoot.dataset.apexMember === 'true'
            : false
        };
        const minCents = records.reduce((min, record) => {
          const cents = getVariantViewModel(record, config).pricing.currentPriceCents;
          return min === null || cents < min ? cents : min;
        }, null);
        const fromPrice = root.querySelector('[data-public-price]')
          || root.querySelector('[data-member-price]');
        if (minCents !== null && fromPrice) {
          fromPrice.textContent = `From ${formatMoneyCents(
            minCents,
            root.dataset.moneyFormat || '{{amount}}'
          )}`;
          fromPrice.hidden = false;
          pricingRoot.hidden = false;
        }
      }
      const addonTotalLine = root.querySelector('[data-addon-total]');
      if (addonTotalLine) {
        addonTotalLine.textContent = '';
        addonTotalLine.hidden = true;
      }
      if (availability) {
        availability.textContent = '';
        availability.hidden = false;
      }
      if (stickyInventory) stickyInventory.textContent = '';
      if (stickyPrice) {
        stickyPrice.textContent = '';
        stickyPrice.hidden = true;
      }
      if (stickyContext) {
        stickyContext.textContent = '';
        stickyContext.hidden = true;
      }
      if (affirmElement) {
        affirmElement.removeAttribute('data-amount');
        affirmElement.hidden = true;
      }
      renderPurchaseActionState(root);
      return null;
    }
    const win = windowRef || (typeof window !== 'undefined' ? window : {});
    const pricingRoot = root.querySelector('[data-pdp-price]');
    const pricingDataset = pricingRoot ? pricingRoot.dataset : {};
    const presentation = getVariantViewModel(variant, {
      addonTotalCents: getAddonTotalCents(root),
      apexDiscountPercentage: variant.apexDiscountPercentage
        ?? pricingDataset.apexDiscount,
      dealerDiscountPercentage: pricingDataset.dealerDiscount,
      isApexMember: pricingDataset.apexMember === 'true'
    });
    const moneyFormat = root.dataset.moneyFormat || '{{amount}}';
    const currentPrice = formatMoneyCents(
      presentation.pricing.currentPriceCents,
      moneyFormat
    );
    const idInput = root.querySelector('form.product-form input[name="id"]');
    const sku = root.querySelector('.product-single__sku');
    const publicPrice = root.querySelector('[data-public-price]');
    const memberPrice = root.querySelector('[data-member-price]');
    const originalPrice = root.querySelector('[data-original-price]');
    const savings = root.querySelector('[data-member-savings]');
    const stickyPrice = root.querySelector('[data-pdp-sticky-price]');
    const stickyContext = root.querySelector('[data-pdp-sticky-context]');
    const stickyInventory = root.querySelector('[data-pdp-sticky-inventory]');
    const availability = root.querySelector('[data-pdp-availability]');

    root.dataset.selectedVariantId = String(variant.id);
    root.dataset.selectedMediaId = presentation.featuredMediaId
      ? String(presentation.featuredMediaId)
      : '';
    root.dataset.variantAvailable = String(variant.available === true);
    root.dataset.variantInventoryPolicy = String(
      variant.policy || variant.inventory_policy || ''
    );
    // Liquid gets the last word on the commit action when it said so. A
    // subscription's label is not derived from inventory, and a customer who
    // already subscribes must not have the button re-enabled underneath them
    // by a variant render.
    const labelOverride = root.dataset.purchaseLabelOverride;
    const purchaseLocked = root.dataset.purchaseLocked === 'true';
    root.dataset.variantPurchasable = purchaseLocked
      ? 'false'
      : String(!presentation.purchase.disabled);
    root.dataset.purchaseLabel = labelOverride || presentation.purchase.text;
    const variantQuantity = typeof variant.quantity === 'number'
      ? variant.quantity
      : variant.inventory_quantity;
    root.dataset.variantQuantity = Number.isFinite(Number(variantQuantity))
      ? String(variantQuantity)
      : '';
    // Derived from state shape, not label text: the in-stock label
    // carries a count ("7 in stock" / "10+ in stock") and must never be
    // string-matched. A purchasable state that carries a distinct
    // compact form is the backorder state (continue-policy at zero).
    root.dataset.variantState = presentation.purchase.disabled
      ? 'sold-out'
      : presentation.inventoryCompact !== presentation.inventory
        ? 'available-to-order'
        : 'available';
    if (pricingRoot) {
      pricingRoot.dataset.variantState = root.dataset.variantState;
      pricingRoot.hidden = false;
    }
    if (idInput) idInput.value = String(variant.id);
    if (sku) {
      sku.textContent = presentation.sku ? `SKU: ${presentation.sku}` : '';
      sku.hidden = presentation.sku === '';
    }
    if (publicPrice) {
      publicPrice.textContent = currentPrice;
      publicPrice.hidden = false;
    }
    if (memberPrice) {
      memberPrice.textContent = currentPrice;
      memberPrice.hidden = false;
    }
    if (
      originalPrice
      && Object.prototype.hasOwnProperty.call(presentation.pricing, 'originalPriceCents')
    ) {
      originalPrice.textContent = formatMoneyCents(
        presentation.pricing.originalPriceCents,
        moneyFormat
      );
      originalPrice.hidden = false;
    } else if (originalPrice) {
      originalPrice.textContent = '';
      originalPrice.hidden = true;
    }
    if (
      savings
      && Object.prototype.hasOwnProperty.call(presentation.pricing, 'savingsCents')
    ) {
      savings.textContent = `Save ${formatMoneyCents(
        presentation.pricing.savingsCents,
        moneyFormat
      )}`;
      savings.hidden = false;
    } else if (savings) {
      savings.textContent = '';
      savings.hidden = true;
    }
    if (stickyPrice) {
      stickyPrice.textContent = currentPrice;
      stickyPrice.hidden = false;
    }
    if (stickyContext) {
      stickyContext.textContent = presentation.sku ? `SKU: ${presentation.sku}` : '';
      stickyContext.hidden = presentation.sku === '';
    }
    if (availability) {
      availability.textContent = presentation.inventory;
      availability.hidden = presentation.inventory === '';
    }
    if (stickyInventory) {
      stickyInventory.textContent = presentation.inventoryCompact;
    }
    const addonTotalLine = root.querySelector('[data-addon-total]');
    const addonAddAll = root.querySelector('[data-addon-add-all]');
    const hasBundle = Object.prototype.hasOwnProperty.call(
      presentation.pricing,
      'bundleTotalCents'
    );
    const checkedAddonCount = root.querySelectorAll('.addon-checkbox:checked').length;
    const bundleCount = checkedAddonCount + 1;
    if (addonTotalLine) {
      if (hasBundle) {
        addonTotalLine.textContent = `Price for all ${bundleCount}: ${formatMoneyCents(
          presentation.pricing.bundleTotalCents,
          moneyFormat
        )}`;
        addonTotalLine.hidden = false;
      } else {
        addonTotalLine.textContent = '';
        addonTotalLine.hidden = true;
      }
    }
    if (addonAddAll) {
      const purchaseBlocked = presentation.purchase.disabled === true;
      addonAddAll.textContent = `Add all ${bundleCount} to cart`;
      addonAddAll.disabled = purchaseBlocked;
      addonAddAll.hidden = !hasBundle || checkedAddonCount === 0 || purchaseBlocked;
    }
    renderPurchaseActionState(root);

    const affirmElement = root.querySelector('.affirm-as-low-as');
    if (affirmElement) {
      affirmElement.hidden = false;
      affirmElement.setAttribute(
        'data-amount',
        String(presentation.pricing.currentPriceCents)
      );
      if (win.affirm && win.affirm.ui && typeof win.affirm.ui.ready === 'function') {
        win.affirm.ui.ready(() => {
          if (typeof win.affirm.ui.refresh === 'function') win.affirm.ui.refresh();
        });
      }
    }

    if (presentation.featuredMediaId) {
      const mediaControl = root.querySelector(
        `[aria-controls$="-media-${presentation.featuredMediaId}"]`
      );
      if (mediaControl && typeof mediaControl.click === 'function') mediaControl.click();
    }
    if (win.history && win.location) {
      try {
        const url = new URL(win.location.href);
        const isInitialRender = root.dataset.pdpInitialVariantRender === 'true';
        if (isInitialRender) delete root.dataset.pdpInitialVariantRender;
        const urlVariant = url.searchParams.get('variant');
        // Do not add ?variant= to a clean URL on first render; only correct
        // an existing param or record a real selection change.
        if ((!isInitialRender || urlVariant) && urlVariant !== String(variant.id)) {
          url.searchParams.set('variant', variant.id);
          win.history.replaceState({}, '', url.toString());
        }
      } catch (error) {}
    }
    if (typeof root.saikoUpdateEta === 'function') root.saikoUpdateEta();
    return presentation;
  }

  function initializeVariantCoordinator(root, documentRef, windowRef) {
    if (!root || root.dataset.variantCoordinatorInitialized === 'true') return null;
    const doc = documentRef || document;
    const win = windowRef || window;
    const productId = String(root.dataset.productId || '');
    const elementListeners = [];
    let renderVersion = 0;

    const schedule = (variant) => {
      const requestedVersion = ++renderVersion;
      if (!variant) {
        const clear = () => {
          if (requestedVersion === renderVersion) renderVariantState(root, null, win);
        };
        if (typeof win.requestAnimationFrame === 'function') win.requestAnimationFrame(clear);
        else clear();
        return;
      }
      const inventory = win.inventories && win.inventories[productId];
      const state = inventory ? inventory[String(variant.id)] : null;
      if (!state) return;
      const render = () => {
        if (requestedVersion === renderVersion) renderVariantState(root, state, win);
      };
      if (typeof win.requestAnimationFrame === 'function') win.requestAnimationFrame(render);
      else render();
    };

    const onVariantChanged = (event) => {
      schedule(event && event.detail);
    };
    doc.addEventListener('variant:changed', onVariantChanged);
    const inventory = win.inventories && win.inventories[productId];
    const annotateOptionAvailability = () => {
      const records = inventory ? Object.values(inventory) : [];
      if (records.length === 0) return;
      const selects = Array.from(
        root.querySelectorAll('.product-form__option-select')
      );
      const selectedOptions = selects.map((optionSelect) => optionSelect.value);
      selects.forEach((select, optionIndex) => {
        if (!select.options || typeof select.options.length !== 'number') return;
        const availability = getOptionValueAvailability(
          records,
          optionIndex,
          selectedOptions
        );
        Array.from(select.options).forEach((option) => {
          if (!option || typeof option.value !== 'string' || option.value === '') return;
          if (option.dataset && option.dataset.baseLabel == null) {
            option.dataset.baseLabel = String(option.textContent || '').trim();
          }
          const baseLabel = option.dataset
            ? option.dataset.baseLabel
            : option.textContent;
          const isAvailable = availability[option.value] !== false;
          option.textContent = isAvailable
            ? baseLabel
            : `${baseLabel} (unavailable)`;
        });
      });
    };
    root.querySelectorAll('.product-form__option-select').forEach((select) => {
      const onChange = () => {
        const selectedOptions = Array.from(
          root.querySelectorAll('.product-form__option-select')
        ).map((optionSelect) => optionSelect.value);
        const variant = findVariantByOptions(inventory, selectedOptions);
        annotateOptionAvailability();
        doc.dispatchEvent(new win.CustomEvent('variant:changed', { detail: variant }));
      };
      select.addEventListener('change', onChange);
      elementListeners.push([select, 'change', onChange]);
    });
    annotateOptionAvailability();
    root.querySelectorAll('.addon-checkbox').forEach((checkbox) => {
      const onChange = () => {
        const idInput = root.querySelector('form.product-form input[name="id"]');
        schedule(idInput ? { id: idInput.value } : null);
      };
      checkbox.addEventListener('change', onChange);
      elementListeners.push([checkbox, 'change', onChange]);
    });
    const idInput = root.querySelector('form.product-form input[name="id"]');
    root.dataset.pdpInitialVariantRender = 'true';
    schedule(idInput && inventory ? inventory[String(idInput.value)] : null);
    root.dataset.variantCoordinatorInitialized = 'true';
    return {
      schedule,
      destroy() {
        renderVersion += 1;
        doc.removeEventListener('variant:changed', onVariantChanged);
        elementListeners.forEach(([element, name, handler]) => {
          element.removeEventListener(name, handler);
        });
        delete root.dataset.variantCoordinatorInitialized;
      }
    };
  }

  function getFitmentPresentation(state, vehicle, destinations) {
    const status = state && state.status ? state.status : 'unavailable';
    const selectedVehicle = vehicleLabel(vehicle);
    const urls = destinations || {};
    const productDetailsUrl = urls.productDetailsUrl || '#product-details';
    const vehicleSelectorUrl = urls.vehicleSelectorUrl || '';
    const presentations = {
      confirmed: {
        status: 'confirmed',
        title: `Guaranteed to fit your ${selectedVehicle}`,
        evidence: "You've got our fitment guarantee.",
        primaryAction: 'Change vehicle',
        primaryActionKind: 'vehicle',
        primaryActionHref: vehicleSelectorUrl
      },
      no_vehicle: {
        status: 'no_vehicle',
        title: 'Check fitment for your vehicle',
        evidence: '',
        primaryAction: 'Select your vehicle',
        primaryActionKind: 'vehicle',
        primaryActionHref: vehicleSelectorUrl
      },
      not_confirmed: {
        status: 'not_confirmed',
        title: `Not confirmed for your ${selectedVehicle}`,
        evidence: '',
        primaryAction: 'Change vehicle',
        primaryActionKind: 'vehicle',
        primaryActionHref: vehicleSelectorUrl
      },
      universal: {
        status: 'universal',
        title: 'Universal fitment',
        evidence: 'Confirm dimensions, connections, and product specifications before ordering.',
        primaryAction: 'Review dimensions and connections',
        primaryActionKind: 'product_details',
        primaryActionHref: productDetailsUrl
      },
      unavailable: {
        status: 'unavailable',
        title: 'Fitment not listed',
        evidence: '',
        primaryAction: 'Ask a build expert',
        primaryActionKind: 'expert',
        primaryActionHref: ''
      },
      wheels: {
        status: 'wheels',
        title: 'Fits by size, bolt pattern, and offset',
        evidence: 'If you have questions about wheel fitment, ask a build expert.',
        primaryAction: vehicle ? 'Change vehicle' : 'Select your vehicle',
        primaryActionKind: 'vehicle',
        primaryActionHref: vehicleSelectorUrl
      },
      fastener: {
        status: 'fastener',
        title: 'Fits by thread pitch and seat',
        evidence: 'If you have questions about which fastener you need, ask a build expert.',
        primaryAction: vehicle ? 'Change vehicle' : 'Select your vehicle',
        primaryActionKind: 'vehicle',
        primaryActionHref: vehicleSelectorUrl
      }
    };
    return presentations[status] || presentations.unavailable;
  }

  /*
    The spec verdict for a wheel or a fastener.

    These products carry no chassis rows, so the vehicle-state machine had
    nothing to read and was answering "Fitment not listed" or "Not confirmed
    for your 2020 Civic Si" on 5,204 wheels and 104 lug nuts, which is worse
    than silence on a store selling a fitment guarantee. Both answers are spec
    answers and both specs now exist.

    The discipline is updateVehicleSpecLine()'s, on the browse surfaces:

      - only claim a match when BOTH sides are known
      - a vehicle whose ratified row deliberately states no bolt pattern gets
        no bolt-pattern claim (the 2002-2003 STI)
      - no row at all means say nothing
      - state the spec that matches, never that the product fits the car.
        Diameter, width, offset and hub clearance decide whether a wheel rubs.
        The guarantee cannot back that claim and the table was never built to
        support it, so the caveat is said out loud on the positive case, which
        is where an over-read costs a return.

    Dual-drilled wheels carry two patterns and fit if EITHER matches. 85 of
    them. A naive equality check calls a 5x100/5x114.3 wheel a mismatch for
    half the cars it fits.
  */
  function getSpecVerdict(spec, row, selectedVehicle, routes) {
    const kind = spec && spec.kind === 'fastener' ? 'fastener' : 'wheels';
    const patterns = spec && Array.isArray(spec.patterns)
      ? spec.patterns.filter(Boolean).map(String)
      : [];
    const thread = spec && spec.thread ? String(spec.thread) : '';
    const label = vehicleLabel(selectedVehicle);
    const routeMap = routes || {};

    if (kind === 'fastener') {
      // The Supra takes wheel BOLTS. Every lug nut on the site is the wrong
      // part for that car, whatever its thread pitch, so the thread comparison
      // is not the question to answer.
      if (row && row.f === 'bolt' && label) {
        return {
          status: 'spec_mismatch',
          title: `Your ${label} takes wheel bolts, not lug nuts.`,
          evidence: 'Ask a build expert and we will get you the right fastener.',
          primaryAction: 'Change vehicle',
          primaryActionKind: 'vehicle',
          lineText: `Your ${label} takes wheel bolts, not lug nuts.`
        };
      }
      if (!thread || !row || !row.t || !label) return null;
      if (thread === row.t) {
        return {
          status: 'spec_match',
          title: `${thread}. The right thread for your ${label}.`,
          evidence: 'Check the seat type and length against your wheels.',
          primaryAction: 'Change vehicle',
          primaryActionKind: 'vehicle',
          lineText: `${thread}. The right thread for your ${label}. Check the seat type and length against your wheels.`
        };
      }
      return {
        status: 'spec_mismatch',
        title: `These are ${thread}. Your ${label} takes ${row.t}.`,
        evidence: '',
        primaryAction: 'Change vehicle',
        primaryActionKind: 'vehicle',
        routeLabel: `Show ${row.t} lug nuts`,
        routeHref: `/collections/lug-nuts?filter.p.m.custom.lug_thread_pitch=${encodeURIComponent(row.t)}`,
        lineText: `These are ${thread}. Your ${label} takes ${row.t}.`
      };
    }

    if (!patterns.length || !row || !row.p || !label) return null;

    if (patterns.indexOf(row.p) !== -1) {
      // Name the pattern that matched, not the whole drilling: it is the one
      // that answers the question.
      return {
        status: 'spec_match',
        title: `${row.p}. Bolts to your ${label}.`,
        evidence: 'Check width and offset for clearance.',
        primaryAction: 'Change vehicle',
        primaryActionKind: 'vehicle',
        lineText: `${row.p}. Bolts to your ${label}. Check width and offset for clearance.`
      };
    }

    const stated = patterns.join(' and ');
    const route = routeMap[row.p];
    return {
      status: 'spec_mismatch',
      title: `These are ${stated} wheels. Your ${label} takes ${row.p}.`,
      evidence: '',
      primaryAction: 'Change vehicle',
      primaryActionKind: 'vehicle',
      routeLabel: route ? `Show ${row.p} wheels` : '',
      routeHref: route ? `/collections/${route}` : '',
      lineText: `These are ${stated} wheels. Your ${label} takes ${row.p}.`
    };
  }

  // Exact make, exact model, year inside the row's range. No fuzzy matching
  // and no nearest year: a 2001 Civic is 4x100 and a 2006 Civic is 5x114.3,
  // so there is no sensible answer for a year between rows and therefore no
  // answer at all.
  function resolveVehicleSpecRow(rows, selectedVehicle) {
    if (!selectedVehicle || !Array.isArray(rows)) return null;
    const year = Number(selectedVehicle.year);
    const make = String(selectedVehicle.make || '').trim();
    let model = String(selectedVehicle.model || '').trim();
    const submodel = String(selectedVehicle.submodel || '').trim();
    if (submodel && model.indexOf(submodel) === -1) model = `${model} ${submodel}`.trim();
    if (!year || !make || !model) return null;
    return rows.find((row) => row.mk === make && row.md === model
      && year >= row.a && year <= row.b) || null;
  }

  // One accessible-modal lifecycle (reparent to body, scroll lock, focus
  // trap, Escape, opener restore) shared by every PDP modal.
  function createModalController(root, documentRef, config) {
    if (!root || root.dataset[config.initializedFlag] === 'true') return null;
    const modal = root.querySelector(config.modalSelector);
    const openers = Array.from(root.querySelectorAll(config.openSelector));
    if (!modal || openers.length === 0) return null;

    const dialog = modal.querySelector('[role="dialog"]');
    const closeControls = Array.from(
      modal.querySelectorAll(config.closeSelector)
    );
    const documentElement = documentRef.documentElement;
    const homeParent = modal.parentNode;
    const homeNextSibling = modal.nextSibling;
    let opener = null;
    let previousOverflow = '';
    let isOpen = false;

    const moveModalToBody = () => {
      if (documentRef.body && modal.parentNode !== documentRef.body) {
        documentRef.body.appendChild(modal);
      }
    };
    const restoreModalHome = () => {
      if (!homeParent || modal.parentNode === homeParent) return;
      if (homeNextSibling && homeNextSibling.parentNode === homeParent) {
        homeParent.insertBefore(modal, homeNextSibling);
      } else {
        homeParent.appendChild(modal);
      }
    };
    const getFocusable = () => Array.from(modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), '
      + 'select:not([disabled]), textarea:not([disabled]), '
      + '[tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.hidden !== true);
    const focusDialog = () => {
      const first = getFocusable()[0];
      if (first && typeof first.focus === 'function') first.focus();
      else if (dialog && typeof dialog.focus === 'function') dialog.focus();
    };
    const open = (event) => {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (isOpen) return;
      opener = event && event.currentTarget ? event.currentTarget : openers[0];
      moveModalToBody();
      modal.hidden = false;
      isOpen = true;
      previousOverflow = documentElement && documentElement.style
        ? documentElement.style.overflow
        : '';
      if (documentElement && documentElement.style) {
        documentElement.style.overflow = 'hidden';
      }
      focusDialog();
    };
    const close = (event, restoreFocus = true) => {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      if (!isOpen && modal.hidden) return;
      modal.hidden = true;
      isOpen = false;
      if (documentElement && documentElement.style) {
        documentElement.style.overflow = previousOverflow;
      }
      restoreModalHome();
      if (restoreFocus && opener && typeof opener.focus === 'function') opener.focus();
      opener = null;
    };
    const onKeydown = (event) => {
      if (!isOpen || modal.hidden) return;
      if (event.key === 'Escape') {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        close(null, true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        focusDialog();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = documentRef.activeElement;
      if (
        (!event.shiftKey && active === last)
        || (event.shiftKey && active === first)
        || !focusable.includes(active)
      ) {
        if (typeof event.preventDefault === 'function') event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    const onClose = (event) => close(event, true);

    openers.forEach((control) => control.addEventListener('click', open));
    closeControls.forEach((control) => control.addEventListener('click', onClose));
    documentRef.addEventListener('keydown', onKeydown);
    root.dataset[config.initializedFlag] = 'true';

    return {
      open,
      close,
      destroy() {
        close(null, false);
        openers.forEach((control) => control.removeEventListener('click', open));
        closeControls.forEach((control) => {
          control.removeEventListener('click', onClose);
        });
        documentRef.removeEventListener('keydown', onKeydown);
        delete root.dataset[config.initializedFlag];
      }
    };
  }

  function initializeApexModal(root, documentRef) {
    return createModalController(root, documentRef, {
      modalSelector: '.js-apex-learn-more-modal',
      openSelector: '.js-apex-learn-more-open',
      closeSelector: '.js-apex-learn-more-close',
      initializedFlag: 'apexModalInitialized'
    });
  }

  // The Fitment guarantee link opens the explainer in place; its href
  // remains the full policy page as the no-script path.
  function initializeGuaranteeModal(root, documentRef) {
    return createModalController(root, documentRef, {
      modalSelector: '.js-pdp-guarantee-modal',
      openSelector: '.js-pdp-guarantee-open',
      closeSelector: '.js-pdp-guarantee-close',
      initializedFlag: 'guaranteeModalInitialized'
    });
  }

  function openShopifyInbox(trigger, documentRef, windowRef) {
    const doc = documentRef || (typeof document !== 'undefined' ? document : null);
    const view = windowRef
      || (doc && doc.defaultView)
      || (typeof window !== 'undefined' ? window : null);
    const resolution = doc
      ? resolveShopifyChat(doc, view)
      : { activator: null, chat: null };
    const { activator, chat } = resolution;
    const fallbackId = trigger && trigger.dataset
      ? trigger.dataset.chatFallbackId
      : '';
    const feedback = fallbackId
      && doc
      && typeof doc.getElementById === 'function'
      ? doc.getElementById(fallbackId)
      : null;
    const setFeedback = (state, message) => {
      if (trigger && trigger.dataset) trigger.dataset.chatState = state;
      if (feedback) {
        feedback.textContent = message;
        feedback.hidden = message === '';
      }
      if (
        message
        && fallbackId
        && trigger
        && typeof trigger.setAttribute === 'function'
      ) {
        trigger.setAttribute('aria-describedby', fallbackId);
      } else if (
        !message
        && trigger
        && typeof trigger.removeAttribute === 'function'
      ) {
        trigger.removeAttribute('aria-describedby');
      }
    };

    if (activator && typeof activator.click === 'function') {
      setFeedback('opened', '');
      activator.click();
      return true;
    }

    if (chat && typeof chat.scrollIntoView === 'function') {
      chat.scrollIntoView({ block: 'nearest' });
    }

    if (chat) {
      setFeedback(
        'launcher',
        'Chat is available from the Shopify Inbox launcher on this page.'
      );
    } else {
      setFeedback(
        'unavailable',
        'Chat is unavailable right now. Please try again in a moment.'
      );
    }
    return false;
  }

  // The generated ratified table, inlined by snippets/saikospeed-vehicle-specs
  // .liquid. Parsed once per block and cached on the element: it is 62 rows of
  // static data and the verdict re-renders on every variant change.
  function readVehicleSpecTable(fitment) {
    if (fitment.__saikoVehicleSpecs) return fitment.__saikoVehicleSpecs;
    const documentRef = fitment.ownerDocument;
    const island = documentRef && typeof documentRef.querySelector === 'function'
      ? documentRef.querySelector('[data-saikospeed-vehicle-specs]')
      : null;
    let parsed = null;
    try {
      parsed = island ? JSON.parse(island.textContent || '') : null;
    } catch (error) {
      parsed = null;
    }
    fitment.__saikoVehicleSpecs = {
      rows: parsed && Array.isArray(parsed.rows) ? parsed.rows : [],
      routes: (parsed && parsed.routes) || {}
    };
    return fitment.__saikoVehicleSpecs;
  }

  // The option combination the shopper has selected has no variant behind it.
  const NO_VARIANT = 'none';

  // The product's own spec. Bolt pattern is per VARIANT, so the answer follows
  // the variant the shopper is actually looking at: an Aodhan product spans
  // several sizes and drillings and a product-level read would be wrong for
  // most of them.
  function readProductSpec(fitment, root) {
    const node = fitment.querySelector('[data-pdp-spec-data]');
    if (!node) return null;
    let payload = null;
    try {
      payload = JSON.parse(node.textContent || '');
    } catch (error) {
      return null;
    }
    if (!payload) return null;

    const kind = payload.kind === 'fastener' ? 'fastener' : 'wheels';
    const thread = payload.thread || '';

    // An option combination with no variant behind it has no bolt pattern to
    // judge, so the verdict stands down rather than leaving the answer for the
    // variant they just left standing over the options they are now looking
    // at. Thread pitch is product-level and unaffected.
    if (fitment.dataset.specVariantId === NO_VARIANT) {
      return { kind, patterns: [], thread };
    }

    const variants = Array.isArray(payload.variants) ? payload.variants : [];
    // The variant:changed detail is the authority when there is one: the id
    // input is updated by a later, asynchronous render pass, so reading it
    // here would answer for the variant they just left.
    const idInput = root.querySelector('form.product-form input[name="id"]');
    const currentId = fitment.dataset.specVariantId
      || (idInput ? String(idInput.value) : '');
    const match = variants.find((entry) => String(entry.id) === currentId)
      || (variants.length === 1 ? variants[0] : null);
    // The product-level field is the fallback for the 897 published wheels in
    // the pattern collections whose titles never stated a size, so their
    // variants carry nothing. It answers for all of them.
    const raw = (match && match.p) || payload.product || null;
    const patterns = Array.isArray(raw) ? raw : (raw ? [raw] : []);

    return { kind, patterns, thread };
  }

  function renderFitment(root, fitmentApi, cookieString) {
    const fitment = root.querySelector('[data-pdp-fitment]');
    const payloadNode = root.querySelector('[data-pdp-fitment-data]');
    if (!fitment) return false;

    let payload = [];
    let entries = [];
    let vehicle = null;
    let state = { status: 'unavailable', matchedFitment: null };
    const hasFitmentApi = fitmentApi
      && typeof fitmentApi.parseFitmentEntries === 'function'
      && typeof fitmentApi.parseVehicleCookie === 'function'
      && typeof fitmentApi.resolveFitmentState === 'function';

    if (payloadNode && hasFitmentApi) {
      try {
        payload = JSON.parse(payloadNode.textContent);
        entries = fitmentApi.parseFitmentEntries(payload);
        vehicle = fitmentApi.parseVehicleCookie(cookieString);
        state = fitmentApi.resolveFitmentState(entries, vehicle);
      } catch (error) {
        entries = [];
        vehicle = null;
        state = { status: 'unavailable', matchedFitment: null };
      }
    }

    const title = fitment.querySelector('[data-fitment-title]');
    const evidence = fitment.querySelector('[data-fitment-evidence]');
    const primaryAction = fitment.querySelector('[data-fitment-primary-action]');
    const coverage = fitment.querySelector('[data-fitment-coverage]');
    const coverageList = coverage
      ? coverage.querySelector('[data-fitment-coverage-list]')
      : null;
    const destinations = primaryAction ? {
      productDetailsUrl: primaryAction.dataset.productDetailsUrl,
      vehicleSelectorUrl: primaryAction.dataset.vehicleSelectorUrl
    } : {};
    const formatEntry = hasFitmentApi
      && typeof fitmentApi.formatFitmentEntry === 'function'
      ? fitmentApi.formatFitmentEntry
      : null;
    // Wheels and fasteners fit by specification, not by chassis rows; the
    // Liquid template marks the block and the vehicle-state machine is
    // bypassed entirely.
    const specMode = fitment.dataset ? fitment.dataset.fitmentMode : '';
    const isSpecMode = specMode === 'wheels' || specMode === 'fastener';
    let presentation = isSpecMode
      ? getFitmentPresentation({ status: specMode, matchedFitment: null }, vehicle, destinations)
      : getFitmentPresentation(state, vehicle, destinations);

    if (isSpecMode) {
      // No verdict is the deliberate outcome, not a failure: an unknown
      // pattern, an unknown car or a ratified row that states no pattern all
      // leave the neutral spec copy standing. Nothing is read until the
      // product's own side is known, so the common no-spec case costs nothing.
      const productSpec = readProductSpec(fitment, root);
      if (productSpec && vehicle) {
        const table = readVehicleSpecTable(fitment);
        const verdict = getSpecVerdict(
          productSpec,
          resolveVehicleSpecRow(table.rows, vehicle),
          vehicle,
          table.routes
        );
        if (verdict) {
          presentation = Object.assign({}, presentation, verdict, {
            primaryActionHref: destinations.vehicleSelectorUrl || ''
          });
        }
      }
    }

    const routeLink = fitment.querySelector('[data-fitment-route]');
    if (routeLink) {
      const href = presentation.routeHref || '';
      const routeLabel = presentation.routeLabel || '';
      routeLink.textContent = routeLabel;
      if (href) routeLink.href = href;
      routeLink.hidden = !(href && routeLabel);
    }

    fitment.dataset.fitmentStatus = presentation.status;
    if (title) title.textContent = presentation.title;
    if (evidence) {
      evidence.textContent = presentation.evidence;
      evidence.hidden = presentation.evidence === '';
    }

    if (primaryAction) {
      primaryAction.textContent = presentation.primaryAction;
      primaryAction.dataset.fitmentActionKind = presentation.primaryActionKind;
      primaryAction.hidden = presentation.primaryActionKind === 'expert';
      primaryAction.href = presentation.primaryActionHref;
    }

    const stickyFitment = root.querySelector('[data-pdp-sticky-fitment]');
    if (stickyFitment) {
      stickyFitment.textContent = presentation.title;
      stickyFitment.dataset.fitmentStatus = presentation.status;
    }

    // The guarantee modal opens with the buyer's own answer when their
    // saved vehicle is a confirmed match; otherwise the generic lead in
    // the Liquid markup speaks.
    const guaranteeVehicleLine = root.querySelector('[data-guarantee-vehicle]');
    if (guaranteeVehicleLine) {
      const confirmed = presentation.status === 'confirmed' && Boolean(vehicle);
      guaranteeVehicleLine.textContent = confirmed
        ? `${presentation.title}: order it with confidence.`
        : '';
      guaranteeVehicleLine.hidden = !confirmed;
    }

    // The buy box's vehicle-verdict line speaks only when a saved vehicle
    // resolves to a definite answer; every other state stays silent there
    // and the full fitment block below carries the story.
    const fitmentLine = root.querySelector('[data-pdp-fitment-line]');
    if (fitmentLine) {
      const isVehicleVerdict = presentation.status === 'confirmed'
        || presentation.status === 'not_confirmed'
        || presentation.status === 'spec_match'
        || presentation.status === 'spec_mismatch';
      const showLine = Boolean(vehicle) && isVehicleVerdict;
      // The buy box carries the caveat too. It sits directly above Add to
      // cart, which is where reading "bolts to your car" as "fits your car"
      // actually costs a return.
      fitmentLine.textContent = showLine ? (presentation.lineText || presentation.title) : '';
      fitmentLine.dataset.fitmentStatus = presentation.status;
      fitmentLine.hidden = !showLine;
    }

    if (coverageList) {
      coverageList.replaceChildren();
      entries.forEach((entry) => {
        const row = coverageList.ownerDocument.createElement('li');
        row.dataset.fitmentCoverageRow = '';
        row.dataset.fitmentRaw = entry.raw;
        row.textContent = formatEntry ? formatEntry(entry) : entry.raw;
        coverageList.appendChild(row);
      });
    }
    if (coverage) coverage.hidden = entries.length === 0;
    return true;
  }

  /*
    Setting the vehicle without leaving the product.

    The block's whole job is to answer "does this fit my car", so the answer
    and the question live together: the primary action opens a Year/Make/Model
    cascade inside the block, and the verdict above it re-renders in place.

    THIS WRITES THE VEHICLE COOKIE, which until now only the browse machinery
    did (the home hero deliberately does not, and a test enforces that). It is
    a deliberate addition to the writer set, not a side effect: a vehicle the
    shopper sets here has to survive to the next page, or the verdict they
    just got would be forgotten the moment they navigated. The format matches
    hrllc-grid-v1.js persistSelection exactly, field for field, so the browse
    surfaces read it back as their own.
  */
  const VEHICLE_COOKIE_NAME = 'hrllc_vehicle';
  const VEHICLE_COOKIE_DAYS = 30;

  function writeVehicleCookie(documentRef, selection) {
    const expiry = new Date();
    expiry.setTime(expiry.getTime() + VEHICLE_COOKIE_DAYS * 24 * 60 * 60 * 1000);
    documentRef.cookie = `${VEHICLE_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(selection))}`
      + `;expires=${expiry.toUTCString()};path=/;SameSite=Lax`;
  }

  function clearVehicleCookie(documentRef) {
    documentRef.cookie = `${VEHICLE_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax`;
  }

  function initializeVehiclePicker(root, documentRef, windowRef, onVehicleChange) {
    const picker = root.querySelector('[data-pdp-vehicle-picker]');
    const trigger = root.querySelector('[data-fitment-primary-action]');
    if (!picker || !trigger) return null;

    const fitmentApi = windowRef.SaikoSpeedFitment;
    const tokensNode = picker.querySelector('[data-pdp-vehicle-tokens]');
    if (!fitmentApi || typeof fitmentApi.indexVehicleTokens !== 'function' || !tokensNode) return null;

    let rows = [];
    try {
      rows = fitmentApi.indexVehicleTokens(JSON.parse(tokensNode.textContent));
    } catch (error) {
      rows = [];
    }
    if (!rows.length) return null;

    const fields = {
      year: picker.querySelector('[data-pdp-vehicle-field="year"]'),
      make: picker.querySelector('[data-pdp-vehicle-field="make"]'),
      model: picker.querySelector('[data-pdp-vehicle-field="model"]')
    };
    if (!fields.year || !fields.make || !fields.model) return null;
    const clearButton = picker.querySelector('[data-pdp-vehicle-clear]');

    const saved = typeof fitmentApi.parseVehicleCookie === 'function'
      ? fitmentApi.parseVehicleCookie(documentRef.cookie)
      : null;
    const selection = saved
      ? { year: String(saved.year), make: saved.make, model: saved.model }
      : { year: '', make: '', model: '' };

    const years = () => Array.from(new Set(rows.map((row) => row.year)))
      .sort((a, b) => Number(b) - Number(a));
    const makes = (year) => Array.from(new Set(
      rows.filter((row) => row.year === year).map((row) => row.make)
    )).sort();
    const models = (year, make) => Array.from(new Set(
      rows.filter((row) => row.year === year && row.make === make).map((row) => row.model)
    )).sort();

    const fill = (select, values, current) => {
      select.replaceChildren();
      const placeholder = select.ownerDocument.createElement('option');
      placeholder.value = '';
      placeholder.textContent = 'Select';
      select.appendChild(placeholder);
      values.forEach((value) => {
        const option = select.ownerDocument.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });
      select.value = values.includes(current) ? current : '';
      select.disabled = values.length === 0;
    };

    const paint = () => {
      fill(fields.year, years(), selection.year);
      // A saved vehicle whose year is no longer in the token list still shows
      // in the verdict above; the cascade just rebuilds honestly from what
      // the catalog can offer.
      if (!fields.year.value) {
        selection.year = '';
        selection.make = '';
        selection.model = '';
      }
      fill(fields.make, selection.year ? makes(selection.year) : [], selection.make);
      if (!fields.make.value) {
        selection.make = '';
        selection.model = '';
      }
      fill(fields.model, selection.make ? models(selection.year, selection.make) : [], selection.model);
      if (!fields.model.value) selection.model = '';
      if (clearButton) clearButton.hidden = !(selection.year && selection.make && selection.model);
    };

    const commit = () => {
      if (!(selection.year && selection.make && selection.model)) return;
      // trim and fitment_code stay empty exactly as the browse cascade leaves
      // them when no trim level is offered: the convermax rows bake the
      // submodel into the model string ("Civic Si"), so the model alone is
      // the whole identity.
      writeVehicleCookie(documentRef, {
        year: selection.year,
        make: selection.make,
        model: selection.model,
        trim: '',
        fitment_code: ''
      });
      onVehicleChange();
    };

    const onFieldChange = (key) => () => {
      selection[key] = fields[key].value;
      if (key === 'year') {
        selection.make = '';
        selection.model = '';
      }
      if (key === 'make') selection.model = '';
      paint();
      commit();
    };

    const handlers = ['year', 'make', 'model'].map((key) => {
      const handler = onFieldChange(key);
      fields[key].addEventListener('change', handler);
      return [fields[key], handler];
    });

    const setOpen = (open) => {
      picker.hidden = !open;
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && typeof fields.year.focus === 'function') fields.year.focus();
    };

    const onTrigger = (event) => {
      // Only the vehicle states open the picker. Universal still deep-links to
      // the product details, and the expert state hides this control entirely.
      if (trigger.dataset.fitmentActionKind !== 'vehicle') return;
      event.preventDefault();
      setOpen(picker.hidden);
    };
    trigger.addEventListener('click', onTrigger);

    let onClear = null;
    if (clearButton) {
      onClear = () => {
        selection.year = '';
        selection.make = '';
        selection.model = '';
        clearVehicleCookie(documentRef);
        paint();
        onVehicleChange();
      };
      clearButton.addEventListener('click', onClear);
    }

    paint();
    return {
      setOpen,
      destroy() {
        handlers.forEach(([element, handler]) => element.removeEventListener('change', handler));
        trigger.removeEventListener('click', onTrigger);
        if (clearButton && onClear) clearButton.removeEventListener('click', onClear);
      }
    };
  }

  function renderResearchFitment(root, fitmentApi, cookieString) {
    const payloadNode = root.querySelector('[data-pdp-research-fitment-data]');
    const list = root.querySelector('[data-pdp-research-fitment-list]');
    const status = root.querySelector('[data-pdp-research-fitment-status]');
    const hasFitmentApi = fitmentApi
      && typeof fitmentApi.parseFitmentEntries === 'function'
      && typeof fitmentApi.parseVehicleCookie === 'function'
      && typeof fitmentApi.resolveFitmentState === 'function';

    if (!payloadNode || !list || !hasFitmentApi) return false;

    let entries;
    let selectedVehicle;
    try {
      entries = fitmentApi.parseFitmentEntries(JSON.parse(payloadNode.textContent));
      selectedVehicle = fitmentApi.parseVehicleCookie(cookieString);
    } catch (error) {
      return false;
    }
    if (!entries.length) return false;

    const documentRef = list.ownerDocument;
    list.replaceChildren();
    let hasMatch = false;

    entries.forEach((entry) => {
      const row = documentRef.createElement('li');
      const label = documentRef.createElement('span');
      const fitmentState = selectedVehicle
        ? fitmentApi.resolveFitmentState([entry], selectedVehicle)
        : { status: 'no_vehicle' };
      const isMatch = fitmentState.status === 'confirmed'
        || fitmentState.status === 'universal';

      row.className = 'pdp-research__fitment-row';
      row.dataset.fitmentMatch = isMatch ? 'true' : 'false';
      label.className = 'pdp-research__fitment-label';
      label.textContent = typeof fitmentApi.formatFitmentEntry === 'function'
        ? fitmentApi.formatFitmentEntry(entry)
        : entry.raw;
      row.appendChild(label);

      if (isMatch) {
        const matchLabel = documentRef.createElement('span');
        matchLabel.className = 'pdp-research__fitment-match';
        matchLabel.textContent = 'Matches selected vehicle';
        row.appendChild(matchLabel);
        hasMatch = true;
      }
      list.appendChild(row);
    });

    root.dataset.researchFitmentState = selectedVehicle
      ? (hasMatch ? 'matched' : 'not-matched')
      : 'no-vehicle';

    if (status) {
      status.textContent = selectedVehicle
        ? `Applications matching ${vehicleLabel(selectedVehicle)} are marked below.`
        : '';
      status.hidden = !selectedVehicle;
    }
    return true;
  }

  function syncResearchDisclosures(root, isDesktop) {
    const disclosures = Array.from(
      root.querySelectorAll('[data-pdp-research-disclosure]')
    );
    if (root.dataset.researchDisclosuresInitialized !== 'true') {
      disclosures.forEach((disclosure) => {
        if (
          disclosure.dataset.researchMobileOpen !== 'true'
          && disclosure.dataset.researchMobileOpen !== 'false'
        ) {
          disclosure.dataset.researchMobileOpen =
            disclosure.dataset.researchDefaultOpen === 'true' ? 'true' : 'false';
        }
      });
      root.dataset.researchDisclosuresInitialized = 'true';
    }

    disclosures.forEach((disclosure) => {
      const summary = typeof disclosure.querySelector === 'function'
        ? disclosure.querySelector('[data-pdp-research-summary]')
        : null;
      const desktopHeading = typeof disclosure.querySelector === 'function'
        ? disclosure.querySelector('[data-pdp-research-desktop-heading]')
        : null;

      if (summary) {
        summary.hidden = isDesktop;
        if (isDesktop) {
          summary.setAttribute('aria-hidden', 'true');
          summary.setAttribute('tabindex', '-1');
        } else {
          summary.removeAttribute('aria-hidden');
          summary.removeAttribute('tabindex');
        }
      }
      if (desktopHeading) {
        desktopHeading.hidden = !isDesktop;
        if (isDesktop) {
          desktopHeading.removeAttribute('aria-hidden');
        } else {
          desktopHeading.setAttribute('aria-hidden', 'true');
        }
      }
      disclosure.open = isDesktop
        || disclosure.dataset.researchMobileOpen === 'true';
    });
    return disclosures.length;
  }

  function initializeResearchContent(documentRef, windowRef) {
    const rootStates = new Map();

    const init = (root) => {
      if (!root) return false;

      if (!rootStates.has(root)) {
        const desktopQuery = windowRef.matchMedia
          ? windowRef.matchMedia('(min-width: 990px)')
          : { matches: true };
        const disclosures = Array.from(
          root.querySelectorAll('[data-pdp-research-disclosure]')
        );
        const syncLayout = () => {
          syncResearchDisclosures(root, desktopQuery.matches);
        };
        const disclosureListeners = [];

        disclosures.forEach((disclosure) => {
          if (typeof disclosure.addEventListener !== 'function') return;
          const onToggle = () => {
            if (desktopQuery.matches) {
              if (!disclosure.open) disclosure.open = true;
              return;
            }
            disclosure.dataset.researchMobileOpen = disclosure.open
              ? 'true'
              : 'false';
          };
          disclosure.addEventListener('toggle', onToggle);
          disclosureListeners.push([disclosure, onToggle]);
        });
        if (typeof desktopQuery.addEventListener === 'function') {
          desktopQuery.addEventListener('change', syncLayout);
        } else if (typeof desktopQuery.addListener === 'function') {
          desktopQuery.addListener(syncLayout);
        }
        const askExperts = Array.from(
          root.querySelectorAll('[data-ask-build-expert]')
        );
        const onAskExpert = (event) => {
          openShopifyInbox(event && event.currentTarget, documentRef, windowRef);
        };
        askExperts.forEach((askExpert) => {
          if (typeof askExpert.addEventListener === 'function') {
            askExpert.addEventListener('click', onAskExpert);
          }
        });
        syncLayout();
        root.dataset.pdpResearchInitialized = 'true';
        rootStates.set(root, {
          desktopQuery,
          disclosureListeners,
          syncLayout,
          askExperts,
          onAskExpert
        });
      }

      renderResearchFitment(
        root,
        windowRef.SaikoSpeedFitment,
        documentRef.cookie
      );
      return true;
    };

    const destroy = (root) => {
      const state = rootStates.get(root);
      if (!state) return false;
      state.disclosureListeners.forEach(([disclosure, handler]) => {
        if (typeof disclosure.removeEventListener === 'function') {
          disclosure.removeEventListener('toggle', handler);
        }
      });
      if (state.askExperts) {
        state.askExperts.forEach((askExpert) => {
          if (typeof askExpert.removeEventListener === 'function') {
            askExpert.removeEventListener('click', state.onAskExpert);
          }
        });
      }
      if (typeof state.desktopQuery.removeEventListener === 'function') {
        state.desktopQuery.removeEventListener('change', state.syncLayout);
      } else if (typeof state.desktopQuery.removeListener === 'function') {
        state.desktopQuery.removeListener(state.syncLayout);
      }
      rootStates.delete(root);
      delete root.dataset.pdpResearchInitialized;
      delete root.dataset.researchDisclosuresInitialized;
      return true;
    };

    Array.from(documentRef.querySelectorAll('[data-pdp-research]')).forEach(init);
    return {
      init,
      refresh() {
        rootStates.forEach((state, root) => {
          renderResearchFitment(
            root,
            windowRef.SaikoSpeedFitment,
            documentRef.cookie
          );
        });
      },
      destroy,
      destroyWithin(target) {
        Array.from(rootStates.keys()).forEach((root) => {
          if (
            root === target
            || (target && typeof target.contains === 'function' && target.contains(root))
          ) {
            destroy(root);
          }
        });
      },
      destroyAll() {
        Array.from(rootStates.keys()).forEach(destroy);
      }
    };
  }

  function revealReviews(documentRef, windowRef, targetRef) {
    const target = targetRef || documentRef.getElementById('judgeme_product_reviews');
    if (!target) return false;

    for (let ancestor = target.parentElement; ancestor; ancestor = ancestor.parentElement) {
      if (ancestor.tagName !== 'DETAILS') continue;
      ancestor.open = true;
      if (
        ancestor.dataset
        && typeof ancestor.matches === 'function'
        && ancestor.matches('[data-pdp-research-disclosure]')
      ) {
        ancestor.dataset.researchMobileOpen = 'true';
      }
    }

    const activePanel = target.closest('[role="tabpanel"][data-panel]');
    const tabs = activePanel ? activePanel.closest('[data-products-info-v2]') : null;
    if (activePanel && tabs) {
      const panelName = activePanel.getAttribute('data-panel');
      tabs.querySelectorAll('[role="tab"][data-tab]').forEach((tab) => {
        tab.setAttribute(
          'aria-selected',
          tab.getAttribute('data-tab') === panelName ? 'true' : 'false'
        );
      });
      tabs.querySelectorAll('[role="tabpanel"][data-panel]').forEach((panel) => {
        panel.setAttribute(
          'aria-hidden',
          panel.getAttribute('data-panel') === panelName ? 'false' : 'true'
        );
      });
    }

    const reduceMotion = windowRef.matchMedia
      && windowRef.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });

    const heading = target.querySelector(
      '.jdgm-rev-widg__title, h2, h3, [role="heading"]'
    ) || target;
    const addedTabIndex = !heading.hasAttribute('tabindex');
    if (addedTabIndex) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    if (addedTabIndex) {
      heading.addEventListener('blur', () => {
        heading.removeAttribute('tabindex');
      }, { once: true });
    }

    return true;
  }

  function navigateToReviews(event, summary, documentRef, windowRef) {
    const isModified = event.defaultPrevented
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
      || (typeof event.button === 'number' && event.button !== 0);
    if (isModified || summary.getAttribute('href') !== '#judgeme_product_reviews') {
      return false;
    }

    const target = documentRef.getElementById('judgeme_product_reviews');
    if (!target) return false;

    event.preventDefault();
    return revealReviews(documentRef, windowRef, target);
  }

  function initializeReviewHashNavigation(documentRef, windowRef) {
    const revealCurrentHash = () => {
      if (!windowRef.location
        || windowRef.location.hash !== '#judgeme_product_reviews') {
        return false;
      }
      return revealReviews(documentRef, windowRef);
    };

    windowRef.addEventListener('hashchange', revealCurrentHash);
    revealCurrentHash();
    return {
      revealCurrentHash,
      destroy() {
        windowRef.removeEventListener('hashchange', revealCurrentHash);
      }
    };
  }

  const BROWSER_LIFECYCLE_KEY = '__saikoSpeedPdpLifecycleV1';

  function initializeBrowser(documentRef, windowRef) {
    const existing = windowRef[BROWSER_LIFECYCLE_KEY];
    if (existing && existing.documentRef === documentRef) {
      existing.scan(documentRef);
      return existing;
    }
    if (existing && typeof existing.destroy === 'function') existing.destroy();

    const rootControllers = new Map();
    const researchController = initializeResearchContent(documentRef, windowRef);

    const refresh = (root) => {
      renderFitment(root, windowRef.SaikoSpeedFitment, documentRef.cookie);
      renderRelatedProductFitment(
        root,
        windowRef.SaikoSpeedFitment,
        documentRef.cookie
      );
    };

    const init = (root) => {
      if (!root || rootControllers.has(root)) return false;
      // Fitment is ONE component, never the gate on the section. This used to
      // bail when renderFitment returned false, which it does whenever the
      // block is absent, and a subscription has no vehicle to confirm: the
      // membership page lost its purchase handler, gallery, ETA, variant
      // coordinator and purchase mirror in one go, silently. `scan` already
      // guarantees this is a [data-pdp-root]; renderFitment self-guards.
      renderFitment(root, windowRef.SaikoSpeedFitment, documentRef.cookie);

      root.dataset.pdpInitialized = 'true';
      const reviewSummary = root.querySelector('[data-pdp-review-summary]');
      const askExperts = Array.from(root.querySelectorAll('[data-ask-build-expert]'));
      const onAskExpert = (event) => {
        openShopifyInbox(event && event.currentTarget, documentRef, windowRef);
      };
      askExperts.forEach((askExpert) => {
        askExpert.addEventListener('click', onAskExpert);
      });

      let onReviewSummary = null;
      if (reviewSummary) {
        onReviewSummary = (event) => {
          navigateToReviews(event, reviewSummary, documentRef, windowRef);
        };
        reviewSummary.addEventListener('click', onReviewSummary);
      }

      const etaController = initializeEta(root, documentRef, windowRef);
      const purchaseController = initializePurchaseForm(root, documentRef, windowRef);
      const galleryController = initializeGallery(root, documentRef, windowRef);
      refresh(root);
      const stickyController = initializeStickyPurchase(root, documentRef, windowRef);
      const apexController = initializeApexModal(root, documentRef);
      const guaranteeController = initializeGuaranteeModal(root, documentRef);
      const variantController = initializeVariantCoordinator(root, documentRef, windowRef);
      // Setting a vehicle in the block re-renders the verdict in place, and
      // the dossier's fitment rows with it, so the whole page agrees.
      const vehiclePickerController = initializeVehiclePicker(root, documentRef, windowRef, () => {
        refresh(root);
        if (researchController && typeof researchController.refresh === 'function') {
          researchController.refresh();
        }
      });

      // A wheel's bolt pattern is a VARIANT spec, so the verdict has to follow
      // the size the shopper is looking at. An Aodhan product spans several
      // drillings and one answer for all of them would be wrong for most.
      const specFitment = root.querySelector('[data-pdp-fitment][data-fitment-mode]');
      const onSpecVariantChanged = (event) => {
        const detail = event && event.detail;
        specFitment.dataset.specVariantId = detail && detail.id != null
          ? String(detail.id)
          : NO_VARIANT;
        renderFitment(root, windowRef.SaikoSpeedFitment, documentRef.cookie);
      };
      if (specFitment) documentRef.addEventListener('variant:changed', onSpecVariantChanged);

      rootControllers.set(root, {
        destroy() {
          askExperts.forEach((element) => {
            element.removeEventListener('click', onAskExpert);
          });
          if (reviewSummary && onReviewSummary) {
            reviewSummary.removeEventListener('click', onReviewSummary);
          }
          if (specFitment) documentRef.removeEventListener('variant:changed', onSpecVariantChanged);
          [
            etaController,
            purchaseController,
            galleryController,
            stickyController,
            apexController,
            guaranteeController,
            variantController,
            vehiclePickerController
          ].filter(Boolean).forEach((controller) => {
            if (typeof controller.destroy === 'function') controller.destroy();
          });
          rootControllers.delete(root);
          delete root.dataset.pdpInitialized;
        }
      });
      return true;
    };

    const scan = (target) => {
      if (!target) return;
      if (target.matches && target.matches('[data-pdp-root]')) init(target);
      if (typeof target.querySelectorAll === 'function') {
        target.querySelectorAll('[data-pdp-root]').forEach(init);
      }
      if (target.matches && target.matches('[data-pdp-research]')) {
        researchController.init(target);
      }
      if (typeof target.querySelectorAll === 'function') {
        target.querySelectorAll('[data-pdp-research]').forEach(researchController.init);
      }
    };

    const destroyWithin = (target) => {
      Array.from(rootControllers.entries()).forEach(([root, controller]) => {
        if (
          root === target
          || (target && typeof target.contains === 'function' && target.contains(root))
        ) {
          controller.destroy();
        }
      });
      researchController.destroyWithin(target);
    };

    const onSectionLoad = (event) => scan(event.target);
    const onSectionUnload = (event) => destroyWithin(event.target);
    const onPageShow = () => {
      documentRef.querySelectorAll('[data-pdp-root]').forEach(refresh);
      researchController.refresh();
    };
    const reviewNavigation = initializeReviewHashNavigation(documentRef, windowRef);

    documentRef.addEventListener('shopify:section:load', onSectionLoad);
    documentRef.addEventListener('shopify:section:unload', onSectionUnload);
    windowRef.addEventListener('pageshow', onPageShow);

    const lifecycle = {
      documentRef,
      scan,
      init,
      destroyWithin,
      destroy() {
        Array.from(rootControllers.values()).forEach((controller) => controller.destroy());
        researchController.destroyAll();
        reviewNavigation.destroy();
        documentRef.removeEventListener('shopify:section:load', onSectionLoad);
        documentRef.removeEventListener('shopify:section:unload', onSectionUnload);
        windowRef.removeEventListener('pageshow', onPageShow);
        if (windowRef[BROWSER_LIFECYCLE_KEY] === lifecycle) {
          delete windowRef[BROWSER_LIFECYCLE_KEY];
        }
      }
    };
    windowRef[BROWSER_LIFECYCLE_KEY] = lifecycle;
    scan(documentRef);
    return lifecycle;
  }

  return {
    buildCartItems,
    buildEtaRequestUrl,
    calculateFixedClearance,
    calculateMemberPriceCents,
    createAbortableLatestRequestGate,
    createLatestRequestGate,
    findVariantByOptions,
    formatMoneyCents,
    getOptionValueAvailability,
    getFitmentPresentation,
    getSpecVerdict,
    resolveVehicleSpecRow,
    getPurchaseState,
    getRelatedProductFitmentLabel,
    getVariantViewModel,
    initializeApexModal,
    initializeBrowser,
    initializeEta,
    initializeGallery,
    initializeGuaranteeModal,
    initializePurchaseForm,
    initializeResearchContent,
    initializeStickyPurchase,
    initializeVariantCoordinator,
    initializeVehiclePicker,
    initializeReviewHashNavigation,
    navigateToReviews,
    openShopifyInbox,
    renderRelatedProductFitment,
    renderResearchFitment,
    renderVariantState,
    renderFitment,
    syncResearchDisclosures
  };
});
